// src/keys.ts
var SEGMENT_RE = /^([A-Za-z_][A-Za-z0-9_]*)(?:#(\d+))?$/;
var REPEAT_SEGMENT_SRC = "[A-Za-z_][A-Za-z0-9_]*#\\d+\\.";
var escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function parseKey(key) {
  const segs = key.split(".");
  const field = segs.pop();
  if (field === void 0) {
    throw new Error(`Key r\u1ED7ng kh\xF4ng h\u1EE3p l\u1EC7: ${JSON.stringify(key)}`);
  }
  const repeats = segs.map((s) => {
    const m = SEGMENT_RE.exec(s);
    if (!m) {
      throw new Error(`Key segment kh\xF4ng h\u1EE3p l\u1EC7: ${s} (trong ${key})`);
    }
    const name = m[1];
    const idxStr = m[2];
    return { name, index: idxStr === void 0 ? 0 : Number(idxStr) };
  });
  return { path: segs, field, repeats };
}
function buildKey(repeats, field) {
  return [...repeats.map((r) => `${r.name}#${r.index}`), field].join(".");
}
function rowPrefix(repeats) {
  return repeats.length ? repeats.map((r) => `${r.name}#${r.index}`).join(".") + "." : "";
}
function repeatRows(answers, repeatName) {
  const escaped = escapeRegExp(repeatName);
  const re = new RegExp(`^((?:${REPEAT_SEGMENT_SRC})*)(${escaped}#(\\d+)\\.)`);
  const prefixes = /* @__PURE__ */ new Set();
  for (const key of Object.keys(answers)) {
    const m = re.exec(key);
    if (!m) continue;
    const ancestorPrefix = m[1] ?? "";
    const ownSegment = m[2] ?? "";
    prefixes.add(ancestorPrefix + ownSegment);
  }
  return Array.from(prefixes).sort((a, b) => a.localeCompare(b, void 0, { numeric: true }));
}
var num = (v) => {
  if (v === null || v === void 0 || v === "") return null;
  const n = Number(String(v).replace(/,/g, ".").trim());
  return Number.isFinite(n) ? n : null;
};
var bool = (v) => v === "1" || v === "true" || v === "yes";
var multi = (v) => String(v ?? "").trim().split(/\s+/).filter(Boolean);

// src/audit-helpers.ts
var nf = (n) => (Math.round(Number(n) * 100) / 100).toLocaleString("vi-VN");
var canDuoi = (b) => b === null || b === void 0 ? -Infinity : b;
var canTren = (b) => b === null || b === void 0 ? Infinity : b;
function auditLevel(v, r) {
  if (v === null || v === void 0 || isNaN(v) || !r) return null;
  if (r.err && (v < canDuoi(r.err[0]) || v > canTren(r.err[1]))) return "error";
  if (r.warn && (v < canDuoi(r.warn[0]) || v > canTren(r.warn[1]))) return "warn";
  return "ok";
}
function recLine(r) {
  if (!r || !r.rec) return "";
  const [lo, hi] = r.rec;
  const hiText = hi === Infinity || hi === null || hi === void 0 ? "kh\xF4ng gi\u1EDBi h\u1EA1n" : nf(hi);
  const loText = lo === -Infinity || lo === null || lo === void 0 ? "kh\xF4ng gi\u1EDBi h\u1EA1n" : nf(lo);
  return `${loText} \u2013 ${hiText}${r.u ? " " + r.u : ""}${r.note ? ` (${r.note})` : ""}`;
}
var nowLine = (v, r) => `${nf(v)}${r && r.u ? " " + r.u : ""}`;
function makeAuditContext(answers) {
  const num3 = (key) => {
    const v = parseFloat(answers[key] ?? "");
    return isNaN(v) ? null : v;
  };
  const txt = (key) => answers[key] === void 0 ? "" : String(answers[key]).trim();
  const rows = (repeatName) => repeatRows(answers, repeatName);
  const sumRows = (repeatName, field) => rows(repeatName).reduce((a, p) => a + (parseFloat(answers[p + field] ?? "") || 0), 0);
  const sumOf = (keys) => keys.reduce((a, k) => a + (parseFloat(answers[k] ?? "") || 0), 0);
  return { num: num3, txt, rows, sumRows, sumOf };
}
function makeAdder(findings) {
  const add = (o) => {
    if (o.level === "warn" || o.level === "error") {
      findings.push({ keys: [], ...o });
    }
  };
  const check = (v, r, opts) => {
    const lv = auditLevel(v, r);
    if (lv !== "warn" && lv !== "error") return;
    add({
      level: lv,
      code: opts.code,
      title: opts.title,
      now: opts.nowText || nowLine(v, r),
      rec: recLine(r),
      fix: opts.fix,
      keys: opts.keys ?? []
    });
  };
  return { add, check };
}

// src/expr.ts
var ExprError = class extends Error {
};
var NAME_RE = /^[A-Za-z_][\w.:-]*/;
function tokenize(src) {
  const out = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (c === "'" || c === '"') {
      const end = src.indexOf(c, i + 1);
      if (end < 0) throw new ExprError(`Thi\u1EBFu d\u1EA5u ${c} \u0111\xF3ng chu\u1ED7i`);
      out.push({ k: "str", v: src.slice(i + 1, end) });
      i = end + 1;
      continue;
    }
    if (c === "$" && src[i + 1] === "{") {
      const end = src.indexOf("}", i);
      if (end < 0) throw new ExprError("Thi\u1EBFu d\u1EA5u } \u0111\xF3ng ${\u2026}");
      const name2 = src.slice(i + 2, end).trim();
      if (!/^[\w-]+$/.test(name2)) throw new ExprError(`T\xEAn bi\u1EBFn kh\xF4ng h\u1EE3p l\u1EC7: \${${name2}}`);
      out.push({ k: "var", v: name2 });
      i = end + 1;
      continue;
    }
    const num3 = /^\d+(\.\d+)?|^\.\d+/.exec(src.slice(i));
    if (num3) {
      out.push({ k: "num", v: parseFloat(num3[0]) });
      i += num3[0].length;
      continue;
    }
    if (src.startsWith("..", i)) {
      out.push({ k: "op", v: ".." });
      i += 2;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (two === "!=" || two === "<=" || two === ">=") {
      out.push({ k: "op", v: two });
      i += 2;
      continue;
    }
    if ("=<>+-*(),.".includes(c)) {
      out.push({ k: "op", v: c });
      i++;
      continue;
    }
    const name = NAME_RE.exec(src.slice(i));
    if (name) {
      let n = name[0];
      while (n.endsWith("-") || n.endsWith(".")) n = n.slice(0, -1);
      out.push({ k: "name", v: n });
      i += n.length;
      continue;
    }
    throw new ExprError(`K\xFD t\u1EF1 kh\xF4ng h\u1EE3p l\u1EC7: ${c}`);
  }
  out.push({ k: "end" });
  return out;
}
var LEVELS = [["or"], ["and"], ["=", "!="], ["<", "<=", ">", ">="], ["+", "-"], ["*", "div", "mod"]];
var WORD_OPS = /* @__PURE__ */ new Set(["or", "and", "div", "mod"]);
function parseExpr(src) {
  const toks = tokenize(String(src ?? ""));
  let p = 0;
  const peek = () => toks[p];
  const isOp = (t, v) => (t.k === "op" || t.k === "name" && WORD_OPS.has(t.v)) && t.v === v;
  const expect = (v) => {
    if (!isOp(peek(), v)) throw new ExprError(`Thi\u1EBFu "${v}"`);
    p++;
  };
  const level = (i) => {
    if (i >= LEVELS.length) return unary();
    let left = level(i + 1);
    for (; ; ) {
      const t = peek();
      const op = LEVELS[i].find((o) => isOp(t, o));
      if (!op) return left;
      p++;
      left = { t: "bin", op, l: left, r: level(i + 1) };
    }
  };
  const unary = () => {
    if (isOp(peek(), "-")) {
      p++;
      return { t: "neg", e: unary() };
    }
    return primary();
  };
  const primary = () => {
    const t = peek();
    p++;
    if (t.k === "num") return { t: "num", v: t.v };
    if (t.k === "str") return { t: "str", v: t.v };
    if (t.k === "var") return { t: "var", name: t.v };
    if (t.k === "op" && t.v === ".") return { t: "self" };
    if (t.k === "op" && t.v === "..") return { t: "parent" };
    if (t.k === "op" && t.v === "(") {
      const e = level(0);
      expect(")");
      return e;
    }
    if (t.k === "name") {
      if (isOp(peek(), "(")) {
        p++;
        const args = [];
        if (!isOp(peek(), ")")) {
          for (; ; ) {
            args.push(level(0));
            if (isOp(peek(), ",")) {
              p++;
              continue;
            }
            break;
          }
        }
        expect(")");
        if (!(t.v in FUNCTIONS)) throw new ExprError(`H\xE0m kh\xF4ng \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3: ${t.v}()`);
        return { t: "call", fn: t.v, args };
      }
      if (FIELDS.has(t.v)) return { t: "field", name: t.v };
      throw new ExprError(`Kh\xF4ng hi\u1EC3u "${t.v}"`);
    }
    throw new ExprError("Bi\u1EC3u th\u1EE9c kh\xF4ng \u0111\u1EA7y \u0111\u1EE7");
  };
  const ast = level(0);
  if (peek().k !== "end") throw new ExprError("C\xF2n k\xFD t\u1EF1 th\u1EEBa sau bi\u1EC3u th\u1EE9c");
  return ast;
}
var FIELDS = /* @__PURE__ */ new Set(["name", "label", "category"]);
function lookup(answers, name, prefix) {
  let p = prefix;
  for (; ; ) {
    const k = p + name;
    if (k in answers) return answers[k];
    if (p === "") return void 0;
    const np = p.replace(/[^.]*#\d+\.$/, "");
    if (np === p) return void 0;
    p = np;
  }
}
function isNumeric(s) {
  return s.trim() !== "" && !isNaN(Number(s));
}
function toNum(v) {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  return isNumeric(v) ? Number(v) : NaN;
}
function toBool(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0 && !isNaN(v);
  return v.length > 0;
}
function toStr(v) {
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") {
    if (!isFinite(v)) return "";
    return String(Number(v.toPrecision(12)));
  }
  return v;
}
function equals(a, b) {
  if (typeof a === "boolean" || typeof b === "boolean") return toBool(a) === toBool(b);
  if (typeof a === "number" || typeof b === "number") return toNum(a) === toNum(b);
  if (isNumeric(a) && isNumeric(b)) return Number(a) === Number(b);
  return a === b;
}
var val = (n, ctx) => n ? evalNode(n, ctx) : "";
var str = (n, ctx) => toStr(val(n, ctx));
var num2 = (n, ctx) => toNum(val(n, ctx));
function columnValues(args, ctx) {
  const a = args[0];
  if (!a || a.t !== "var") throw new ExprError("sum()/count() nh\u1EADn \u0111\xFAng m\u1ED9t ${t\xEAn} c\u1ED9t");
  const prefix = ctx.prefix ?? "";
  const tail = new RegExp(`^(?:[\\w-]+#\\d+\\.)*${a.name.replace(/[-]/g, "\\-")}$`);
  const out = [];
  for (const [k, v] of Object.entries(ctx.answers)) {
    if (k.startsWith(prefix) && tail.test(k.slice(prefix.length))) out.push(v);
  }
  return out;
}
var FUNCTIONS = {
  if: (a, c) => toBool(val(a[0], c)) ? val(a[1], c) : val(a[2], c),
  not: (a, c) => !toBool(val(a[0], c)),
  true: () => true,
  false: () => false,
  selected: (a, c) => multi(str(a[0], c)).includes(str(a[1], c)),
  "count-selected": (a, c) => multi(str(a[0], c)).length,
  "selected-at": (a, c) => multi(str(a[0], c))[num2(a[1], c)] ?? "",
  position: (a, c) => {
    if (a[0]?.t !== "parent") throw new ExprError("Ch\u1EC9 h\u1ED7 tr\u1EE3 position(..)");
    return (c.idx ?? 0) + 1;
  },
  "string-length": (a, c) => str(a[0], c).length,
  contains: (a, c) => str(a[0], c).toLowerCase().includes(str(a[1], c).toLowerCase()),
  "starts-with": (a, c) => str(a[0], c).startsWith(str(a[1], c)),
  concat: (a, c) => a.map((n) => str(n, c)).join(""),
  coalesce: (a, c) => {
    const first = str(a[0], c);
    return first !== "" ? first : str(a[1], c);
  },
  number: (a, c) => num2(a[0], c),
  int: (a, c) => Math.trunc(num2(a[0], c)),
  abs: (a, c) => Math.abs(num2(a[0], c)),
  round: (a, c) => {
    const d = a[1] ? num2(a[1], c) : 0;
    const f = 10 ** d;
    return Math.round(num2(a[0], c) * f) / f;
  },
  sum: (a, c) => columnValues(a, c).reduce((s, v) => s + (isNumeric(v) ? Number(v) : 0), 0),
  count: (a, c) => columnValues(a, c).filter((v) => v !== "").length,
  today: (_a, c) => c.today ? c.today() : (/* @__PURE__ */ new Date()).toISOString().slice(0, 10),
  /** `jr:choice-name(giá trị, '${câu}')` — nhãn của lựa chọn trong list của câu đó. */
  "jr:choice-name": (a, c) => {
    const value = str(a[0], c);
    const ref = /^\$\{([\w-]+)\}$/.exec(str(a[1], c).trim());
    const list = ref ? c.listOf?.(ref[1]) : void 0;
    const row = list ? c.lists?.[list]?.find((r) => String(r["name"] ?? "") === value) : void 0;
    return row ? String(row["label"] ?? "") : "";
  },
  /**
   * `choice-value('list', n)` — mã của lựa chọn thứ n (1-based) trong list. Không có trong
   * XLSForm chuẩn: dùng cho bảng lặp dòng cố định (`ui_fixed_list`) để mỗi dòng ghi được
   * mã của chính nó (vd P01…P18), thay vì chỉ có số thứ tự.
   */
  "choice-value": (a, c) => {
    const rows = c.lists?.[str(a[0], c)] ?? [];
    return String(rows[num2(a[1], c) - 1]?.["name"] ?? "");
  }
};
function evalNode(n, ctx) {
  switch (n.t) {
    case "num":
      return n.v;
    case "str":
      return n.v;
    case "var":
      return lookup(ctx.answers, n.name, ctx.prefix ?? "") ?? "";
    case "self":
      return ctx.self ?? "";
    case "parent":
      throw new ExprError('".." ch\u1EC9 d\xF9ng trong position(..)');
    case "field":
      return String(ctx.row?.[n.name] ?? "");
    case "neg":
      return -toNum(evalNode(n.e, ctx));
    case "call":
      return FUNCTIONS[n.fn](n.args, ctx);
    case "bin": {
      if (n.op === "or") return toBool(evalNode(n.l, ctx)) || toBool(evalNode(n.r, ctx));
      if (n.op === "and") return toBool(evalNode(n.l, ctx)) && toBool(evalNode(n.r, ctx));
      const l = evalNode(n.l, ctx);
      const r = evalNode(n.r, ctx);
      switch (n.op) {
        case "=":
          return equals(l, r);
        case "!=":
          return !equals(l, r);
        case "<":
          return toNum(l) < toNum(r);
        case "<=":
          return toNum(l) <= toNum(r);
        case ">":
          return toNum(l) > toNum(r);
        case ">=":
          return toNum(l) >= toNum(r);
        case "+":
          return toNum(l) + toNum(r);
        case "-":
          return toNum(l) - toNum(r);
        case "*":
          return toNum(l) * toNum(r);
        case "div":
          return toNum(l) / toNum(r);
        case "mod":
          return toNum(l) % toNum(r);
      }
      throw new ExprError(`To\xE1n t\u1EED l\u1EA1: ${n.op}`);
    }
  }
}
var AST_CACHE = /* @__PURE__ */ new Map();
function cachedParse(src) {
  let hit = AST_CACHE.get(src);
  if (!hit) {
    try {
      hit = parseExpr(src);
    } catch (e) {
      hit = e instanceof ExprError ? e : new ExprError(String(e));
    }
    if (AST_CACHE.size > 5e3) AST_CACHE.clear();
    AST_CACHE.set(src, hit);
  }
  if (hit instanceof ExprError) throw hit;
  return hit;
}
function exprSyntaxError(src) {
  try {
    cachedParse(String(src ?? "").trim());
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
}
function exprString(src, ctx) {
  try {
    return toStr(evalNode(cachedParse(src), ctx));
  } catch {
    return "";
  }
}
function exprBool(src, ctx, fallback = true) {
  try {
    return toBool(evalNode(cachedParse(src), ctx));
  } catch {
    return fallback;
  }
}

// src/eval.ts
function newCtx(answers, prefix, idx, lists, opts, extra = {}) {
  return { answers, prefix, idx, lists, listOf: opts?.listOf, today: opts?.today, ...extra };
}
function listOfQuestions(rows) {
  const map = /* @__PURE__ */ new Map();
  for (const r of rows ?? []) {
    const m = /^select_(?:one|multiple)\s+(\S+)/.exec(String(r["type"] ?? "").trim());
    if (m && r["name"]) map.set(String(r["name"]), m[1]);
  }
  return (q) => map.get(q);
}
function parentPrefix(prefix) {
  return prefix.replace(/[^.]*#\d+\.$/, "");
}
function resolve(answers, name, prefix = "") {
  let p = prefix;
  for (; ; ) {
    const k = p + name;
    if (k in answers) return answers[k];
    if (p === "") return void 0;
    const np = parentPrefix(p);
    if (np === p) return void 0;
    p = np;
  }
}
function lookupChoice(choices, formKey, list, value, field) {
  const rows = choices?.[formKey]?.[list] ?? [];
  const row = rows.find((x) => String(x["name"] ?? "") === String(value ?? ""));
  return row ? String(row[field] ?? "") : "";
}
var POSITION_RE = /^position\(\.\.\)$/;
var COUNT_SELECTED_RE = /^count-selected\(\s*\$\{([\w-]+)\}\s*\)$/;
var SELECTED_AT_RE = /^selected-at\(\s*\$\{([\w-]+)\}\s*,\s*position\(\.\.\)\s*-\s*1\s*\)$/;
var INSTANCE_RE = /^instance\('([\w-]+)'\)\/root\/item\[name=\$\{([\w-]+)\}\]\/(\w+)$/;
var SUM_RE = /^\s*\$\{[\w- ]+\}(\s*\+\s*\$\{[\w- ]+\})+\s*$/;
var VAR_RE = /^\$\{([\w- ]+)\}$/;
var LEGACY_CALC_RES = [POSITION_RE, COUNT_SELECTED_RE, SELECTED_AT_RE, INSTANCE_RE, SUM_RE, VAR_RE];
function evalExpr(expr, answers, prefix = "", idx, formKey, choices, opts) {
  const e = String(expr ?? "").trim();
  if (!e) return "";
  if (POSITION_RE.test(e)) return String((idx ?? 0) + 1);
  let m = e.match(COUNT_SELECTED_RE);
  if (m) {
    const varName = m[1] ?? "";
    return String(multi(resolve(answers, varName, prefix)).length);
  }
  m = e.match(SELECTED_AT_RE);
  if (m) {
    const varName = m[1] ?? "";
    return multi(resolve(answers, varName, prefix))[idx ?? 0] ?? "";
  }
  m = e.match(INSTANCE_RE);
  if (m) {
    const list = m[1] ?? "";
    const varName = m[2] ?? "";
    const field = m[3] ?? "";
    return lookupChoice(choices, formKey ?? "", list, resolve(answers, varName, prefix), field);
  }
  if (SUM_RE.test(e)) {
    let sum = 0;
    const terms = e.match(/\$\{([\w- ]+)\}/g) ?? [];
    terms.forEach((t) => {
      const varName = t.slice(2, -1).trim();
      const v = parseFloat(resolve(answers, varName, prefix) ?? "");
      if (!isNaN(v)) sum += v;
    });
    return String(Math.round(sum * 100) / 100);
  }
  m = e.match(VAR_RE);
  if (m) {
    const varName = (m[1] ?? "").trim();
    return resolve(answers, varName, prefix) ?? "";
  }
  return exprString(e, newCtx(answers, prefix, idx, choices?.[formKey ?? ""], opts));
}
var REL_RE = /^\s*\$\{([\w- ]+)\}\s*(=|!=|>=|<=|>|<)\s*'?"?([^'"\s]+)'?"?\s*$/;
function evalRelevant(expr, answers, prefix = "", opts) {
  const raw = String(expr ?? "").trim();
  const m = raw.match(REL_RE);
  if (!m) return raw ? exprBool(raw, newCtx(answers, prefix, void 0, opts?.lists, opts), true) : true;
  const varName = (m[1] ?? "").trim();
  const op = m[2] ?? "=";
  const rhs = m[3] ?? "";
  const v = resolve(answers, varName, prefix);
  if (v === void 0) return true;
  if (v === "") return op === "!=";
  const a = parseFloat(v);
  const b = parseFloat(rhs);
  const isNum = !isNaN(a) && !isNaN(b);
  const norm = (x) => String(x).toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
  if (op === "=" && !isNum && String(v) !== rhs && norm(rhs) === "khac" && norm(v).includes("khac")) {
    return true;
  }
  switch (op) {
    case "=":
      return isNum ? a === b : String(v) === rhs;
    case "!=":
      return isNum ? a !== b : String(v) !== rhs;
    case ">":
      return isNum && a > b;
    case ">=":
      return isNum && a >= b;
    case "<":
      return isNum && a < b;
    case "<=":
      return isNum && a <= b;
    default:
      return true;
  }
}
function isLegacyConstraint(expr) {
  const rest = String(expr ?? "").replace(/string-length\(\.\)\s*(>=|>|<=|<|=)\s*'?\d+'?/g, "").replace(/\.\s*(>=|>|<=|<|!=|=)\s*'?-?[\d.]+'?/g, "").replace(/\band\b|[()]/g, "").trim();
  return rest === "";
}
function checkConstraint(expr, value, ctx = {}) {
  const e = String(expr ?? "");
  if (!e || value === "" || value === void 0) return true;
  if (!isLegacyConstraint(e)) {
    return exprBool(
      e.trim(),
      newCtx(ctx.answers ?? {}, ctx.prefix ?? "", void 0, ctx.lists, ctx, { self: String(value) }),
      true
    );
  }
  let ok = true;
  const sl = e.match(/string-length\(\.\)\s*(>=|>|<=|<|=)\s*'?(\d+)'?/g) ?? [];
  sl.forEach((t) => {
    const m = t.match(/(>=|>|<=|<|=)\s*'?(\d+)'?/);
    if (!m) return;
    const op = m[1] ?? "=";
    const n = Number(m[2] ?? "0");
    const L = String(value).length;
    const results = { ">=": L >= n, ">": L > n, "<=": L <= n, "<": L < n, "=": L === n };
    ok = ok && (results[op] ?? true);
  });
  const numChecks = e.replace(/string-length\(\.\)[^)]*/g, "").match(/\.\s*(>=|>|<=|<|!=|=)\s*'?(-?[\d.]+)'?/g) ?? [];
  numChecks.forEach((t) => {
    const m = t.match(/(>=|>|<=|<|!=|=)\s*'?(-?[\d.]+)'?/);
    if (!m) return;
    const op = m[1] ?? "=";
    const n = parseFloat(m[2] ?? "0");
    const v = parseFloat(value);
    if (isNaN(v)) return;
    const results = {
      ">=": v >= n,
      ">": v > n,
      "<=": v <= n,
      "<": v < n,
      "=": v === n,
      "!=": v !== n
    };
    ok = ok && (results[op] ?? true);
  });
  return ok;
}
var LEGACY_CHOICE_FILTER_RE = /^\s*category\s*=\s*\$\{([\w-]+)\}\s*$/;
function legacyChoiceFilterVar(expr) {
  const m = LEGACY_CHOICE_FILTER_RE.exec(String(expr ?? ""));
  return m ? m[1] : null;
}
function choiceAllowed(expr, row, answers, prefix = "", opts) {
  return exprBool(String(expr ?? "").trim(), newCtx(answers, prefix, void 0, opts?.lists, opts, { row }), true);
}
var DEFAULT_CALL_RE = /^\s*[a-z][\w:-]*\s*\(.*\)\s*$/i;
function expressionProblem(column, expr) {
  const e = String(expr ?? "").trim();
  return usesNewEngine(column, e) ? exprSyntaxError(e) : null;
}
function usesNewEngine(column, expr) {
  const e = String(expr ?? "").trim();
  if (!e) return false;
  switch (column) {
    case "relevant":
      return !REL_RE.test(e);
    case "calculation":
    case "repeat_count":
      return !LEGACY_CALC_RES.some((re) => re.test(e));
    case "constraint":
      return !isLegacyConstraint(e);
    case "choice_filter":
      return !legacyChoiceFilterVar(e);
    case "default":
      return DEFAULT_CALL_RE.test(e);
  }
  return false;
}

// src/rules/a-rules.ts
var AGE_BAND_LABELS = [
  "D\u01B0\u1EDBi 3 tu\u1ED5i",
  "3 \u2013 5 tu\u1ED5i",
  "6 \u2013 10 tu\u1ED5i",
  "11 \u2013 15 tu\u1ED5i",
  "16 \u2013 20 tu\u1ED5i",
  "21 \u2013 25 tu\u1ED5i",
  "Tr\xEAn 25 tu\u1ED5i"
];
var CF_BANDS = [
  "cf_under_3",
  "cf_3_to_5",
  "cf_6_to_10",
  "cf_11_to_15",
  "cf_16_to_20",
  "cf_21_to_25",
  "cf_over_25"
];
var VOL_BANDS = [
  "volumn_under_3",
  "volumn_3_to_5",
  "volumn_6_to_10",
  "volumn_11_to_15",
  "volumn_16_to_20",
  "volumn_21_to_25",
  "volumn_over_25"
];
var IC_BANDS = [
  "intercrop_under_3",
  "intercrop_3_to_5",
  "intercrop_6_to_10",
  "intercrop_11_to_15",
  "intercrop_16_to_20",
  "intercrop_21_to_25",
  "intercrop_over_25"
];
function checkOnFarmRules(answers, config, choices) {
  const F = [];
  const c = makeAuditContext(answers);
  const { add, check } = makeAdder(F);
  const rec = config.auditRec;
  const area = c.num("coffee_area");
  const coffeeTrees = c.sumOf([...CF_BANDS]);
  const harvest = c.num("volumn");
  check(c.num("crop_temperature"), rec.temperature, {
    code: "A1",
    title: "Nhi\u1EC7t \u0111\u1ED9 trung b\xECnh n\u0103m ngo\xE0i ng\u01B0\u1EE1ng",
    fix: "\u0110\u1ED1i chi\u1EBFu v\u1EDBi d\u1EEF li\u1EC7u tr\u1EA1m kh\xED t\u01B0\u1EE3ng g\u1EA7n nh\u1EA5t; kh\xF4ng l\u1EA5y nhi\u1EC7t \u0111\u1ED9 c\u1EE7a m\u1ED9t ng\xE0y c\u1EE5 th\u1EC3.",
    keys: ["crop_temperature"]
  });
  const climate = c.txt("crop_climate");
  if (climate && Number(climate) > 4)
    add({
      level: "error",
      code: "A2",
      title: "Lo\u1EA1i kh\xED h\u1EADu kh\xF4ng t\u1ED3n t\u1EA1i \u1EDF Vi\u1EC7t Nam",
      now: lookupChoice(choices, "onfarm", "climate", climate, "label") || `M\xE3 ${climate}`,
      rec: "M\u1ED9t trong 4 l\u1EF1a ch\u1ECDn nh\xF3m nhi\u1EC7t \u0111\u1EDBi",
      fix: 'V\xF9ng c\xE0 ph\xEA T\xE2y Nguy\xEAn h\u1EA7u h\u1EBFt thu\u1ED9c "Nhi\u1EC7t \u0111\u1EDBi \u1EA9m (m\xF9a kh\xF4 ng\u1EAFn)"; v\xF9ng n\xFAi cao L\xE2m \u0110\u1ED3ng c\xF3 th\u1EC3 l\xE0 "Nhi\u1EC7t \u0111\u1EDBi v\xF9ng n\xFAi".',
      keys: ["crop_climate"]
    });
  check(area, rec.farmArea, {
    code: "A3",
    title: "Di\u1EC7n t\xEDch canh t\xE1c ngo\xE0i ng\u01B0\u1EE1ng th\u01B0\u1EDDng g\u1EB7p",
    fix: "X\xE1c nh\u1EADn \u0111\u01A1n v\u1ECB l\xE0 h\xE9c-ta, kh\xF4ng ph\u1EA3i s\xE0o hay m\xE9t vu\xF4ng.",
    keys: ["coffee_area"]
  });
  if (area !== null && area > 0 && coffeeTrees > 0)
    check(coffeeTrees / area, rec.coffeeDensity, {
      code: "A4",
      title: "M\u1EADt \u0111\u1ED9 c\xE0 ph\xEA ngo\xE0i ng\u01B0\u1EE1ng canh t\xE1c",
      fix: `\u0110\u1EBFm l\u1EA1i s\u1ED1 g\u1ED1c theo t\u1EEBng nh\xF3m tu\u1ED5i (\u0111ang l\xE0 ${nf(coffeeTrees)} g\u1ED1c) ho\u1EB7c ki\u1EC3m tra l\u1EA1i di\u1EC7n t\xEDch ${nf(area)} ha.`,
      keys: ["coffee_area", ...CF_BANDS]
    });
  const life = c.num("life_time");
  const pstart = c.num("pruning_constant_start");
  check(life, rec.lifeTime, {
    code: "A5",
    title: "Chu k\u1EF3 s\u1ED1ng d\u1EF1 ki\u1EBFn ngo\xE0i ng\u01B0\u1EE1ng",
    fix: "H\u1ECFi l\u1EA1i m\u1ED1c n\xF4ng h\u1ED9 d\u1EF1 \u0111\u1ECBnh nh\u1ED5 b\u1ECF \u0111\u1EC3 t\xE1i canh, kh\xF4ng ph\u1EA3i tu\u1ED5i hi\u1EC7n t\u1EA1i c\u1EE7a v\u01B0\u1EDDn.",
    keys: ["life_time"]
  });
  check(pstart, rec.pruneStart, {
    code: "A6",
    title: "Tu\u1ED5i b\u1EAFt \u0111\u1EA7u t\u1EC9a c\xE0nh ngo\xE0i ng\u01B0\u1EE1ng",
    fix: "Ghi nh\u1EADn n\u0103m b\u1EAFt \u0111\u1EA7u t\u1EA1o h\xECnh/t\u1EC9a \u0111\u1ECBnh k\u1EF3, th\u01B0\u1EDDng ngay sau khi c\xE2y kh\xE9p t\xE1n.",
    keys: ["pruning_constant_start"]
  });
  if (life !== null && pstart !== null && pstart > life)
    add({
      level: "error",
      code: "A6",
      title: "B\u1EAFt \u0111\u1EA7u t\u1EC9a c\xE0nh sau khi c\xE2y \u0111\xE3 h\u1EBFt chu k\u1EF3 s\u1ED1ng",
      now: `B\u1EAFt \u0111\u1EA7u t\u1EC9a \u1EDF tu\u1ED5i ${nf(pstart)} \xB7 chu k\u1EF3 s\u1ED1ng ${nf(life)} n\u0103m`,
      rec: "Tu\u1ED5i b\u1EAFt \u0111\u1EA7u t\u1EC9a ph\u1EA3i nh\u1ECF h\u01A1n chu k\u1EF3 s\u1ED1ng",
      fix: "M\u1ED9t trong hai \xF4 b\u1ECB nh\u1EADp nh\u1EA7m \u2014 ki\u1EC3m tra l\u1EA1i c\u1EA3 hai.",
      keys: ["pruning_constant_start", "life_time"]
    });
  check(c.num("pruning_constant_percent"), rec.prunePercent, {
    code: "A7",
    title: "T\u1EF7 l\u1EC7 c\xE0nh t\u1EC9a h\u1EB1ng n\u0103m ngo\xE0i ng\u01B0\u1EE1ng",
    fix: "\u0110\xE2y l\xE0 ph\u1EA7n sinh kh\u1ED1i b\u1ECB c\u1EAFt b\u1ECF m\u1ED7i n\u0103m, kh\xF4ng ph\u1EA3i t\u1EF7 l\u1EC7 c\xE2y \u0111\u01B0\u1EE3c t\u1EC9a.",
    keys: ["pruning_constant_percent"]
  });
  const cy = c.num("die_by_havest");
  const cp = c.num("die_by_another");
  check(cy, rec.cullYield, {
    code: "A8",
    title: "T\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF do n\u0103ng su\u1EA5t qu\xE1 cao",
    fix: "N\u1EBFu v\u01B0\u1EDDn \u0111ang t\xE1i canh di\u1EC7n r\u1ED9ng, ghi ch\xFA l\u1EA1i \u0111\u1EC3 ng\u01B0\u1EDDi ki\u1EC3m tra kh\xF4ng coi l\xE0 l\u1ED7i.",
    keys: ["die_by_havest"]
  });
  check(cp, rec.cullPest, {
    code: "A9",
    title: "T\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF do s\xE2u b\u1EC7nh/thi\xEAn tai qu\xE1 cao",
    fix: "X\xE1c nh\u1EADn c\xF3 d\u1ECBch b\u1EC7nh ho\u1EB7c thi\xEAn tai trong ni\xEAn v\u1EE5; n\u1EBFu c\xF3, ghi ch\xFA l\u1EA1i.",
    keys: ["die_by_another"]
  });
  if (cy !== null || cp !== null)
    check((cy ?? 0) + (cp ?? 0), rec.cullTotal, {
      code: "A10",
      title: "T\u1ED5ng t\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF h\u1EB1ng n\u0103m qu\xE1 cao",
      fix: "Hai t\u1EF7 l\u1EC7 n\xE0y c\u1ED9ng l\u1EA1i kh\xF4ng \u0111\u01B0\u1EE3c v\u01B0\u1EE3t 100% s\u1ED1 c\xE2y trong v\u01B0\u1EDDn.",
      keys: ["die_by_havest", "die_by_another"]
    });
  if (harvest !== null && harvest > 0 && area !== null && area > 0)
    check(harvest / area, rec.yieldPerHa, {
      code: "A11",
      title: "N\u0103ng su\u1EA5t tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng",
      fix: "C\xE2u 13 ghi nh\u1EADn s\u1EA3n l\u01B0\u1EE3ng qu\u1EA3 t\u01B0\u01A1i. N\u1EBFu n\xF4ng h\u1ED9 b\xE1o theo nh\xE2n, nh\xE2n l\u1EA1i v\u1EDBi 4,2\u20134,5.",
      keys: ["volumn", "coffee_area"]
    });
  if (harvest !== null && harvest > 0 && coffeeTrees > 0)
    check(harvest / coffeeTrees, rec.yieldPerTree, {
      code: "A12",
      title: "N\u0103ng su\u1EA5t tr\xEAn g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng",
      fix: "Ki\u1EC3m tra ch\xE9o gi\u1EEFa t\u1ED5ng s\u1EA3n l\u01B0\u1EE3ng v\xE0 t\u1ED5ng s\u1ED1 g\u1ED1c \u0111ang khai.",
      keys: ["volumn", ...CF_BANDS]
    });
  CF_BANDS.forEach((cf, i) => {
    const trees = parseFloat(answers[cf] ?? "") || 0;
    const volKey = VOL_BANDS[i] ?? "";
    const pct = parseFloat(answers[volKey] ?? "");
    if (trees > 0 && !isNaN(pct) && pct > 0) {
      const [lo, hi] = config.yieldCurve[i] ?? [0, 100];
      if (pct > hi * 1.3 || pct < lo * 0.7)
        add({
          level: "warn",
          code: "A13",
          title: `Nh\xF3m "${AGE_BAND_LABELS[i]}": t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng l\u1EC7ch \u0111\u01B0\u1EDDng cong n\u0103ng su\u1EA5t`,
          now: `${nf(pct)}% so v\u1EDBi n\u0103m \u0111\u1EA1t cao nh\u1EA5t`,
          rec: `${lo} \u2013 ${hi}% (\u0111\u01B0\u1EDDng cong n\u0103ng su\u1EA5t c\xE0 ph\xEA v\u1ED1i)`,
          fix: "H\u1ECFi l\u1EA1i n\xF4ng h\u1ED9: nh\xF3m tu\u1ED5i n\xE0y cho bao nhi\xEAu ph\u1EA7n tr\u0103m so v\u1EDBi th\u1EDDi k\u1EF3 sung s\u1EE9c nh\u1EA5t c\u1EE7a ch\xEDnh n\xF3.",
          keys: [volKey]
        });
    }
  });
  const pctYoung = parseFloat(answers["volumn_under_3"] ?? "") || 0;
  const pctPeak = parseFloat(answers["volumn_6_to_10"] ?? "") || 0;
  if (pctYoung > 0 && pctPeak > 0 && pctYoung >= pctPeak)
    add({
      level: "error",
      code: "A13",
      title: "Ngh\u1ECBch sinh l\xFD: c\xE2y d\u01B0\u1EDBi 3 tu\u1ED5i cho s\u1EA3n l\u01B0\u1EE3ng \u2265 c\xE2y 6\u201310 tu\u1ED5i",
      now: `D\u01B0\u1EDBi 3 tu\u1ED5i ${nf(pctYoung)}% \xB7 6\u201310 tu\u1ED5i ${nf(pctPeak)}%`,
      rec: "D\u01B0\u1EDBi 3 tu\u1ED5i 0\u201310% \xB7 6\u201310 tu\u1ED5i 90\u2013100%",
      fix: "C\xE0 ph\xEA ch\u1EC9 b\u1EAFt \u0111\u1EA7u cho qu\u1EA3 b\xF3i t\u1EEB n\u0103m th\u1EE9 3. Nhi\u1EC1u kh\u1EA3 n\u0103ng hai \xF4 b\u1ECB nh\u1EADp ng\u01B0\u1EE3c.",
      keys: ["volumn_under_3", "volumn_6_to_10"]
    });
  CF_BANDS.forEach((cf, i) => {
    const trees = parseFloat(answers[cf] ?? "") || 0;
    const volKey = VOL_BANDS[i] ?? "";
    const pct = parseFloat(answers[volKey] ?? "") || 0;
    const band = AGE_BAND_LABELS[i] ?? "";
    if (trees === 0 && pct > 0)
      add({
        level: "error",
        code: "A14",
        title: `Nh\xF3m "${band}" c\xF3 t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng nh\u01B0ng kh\xF4ng c\xF3 c\xE2y`,
        now: `S\u1ED1 c\xE2y 0 \xB7 s\u1EA3n l\u01B0\u1EE3ng ${nf(pct)}%`,
        rec: "C\xF3 s\u1EA3n l\u01B0\u1EE3ng th\xEC ph\u1EA3i c\xF3 s\u1ED1 c\xE2y t\u01B0\u01A1ng \u1EE9ng",
        fix: "\u0110i\u1EC1n s\u1ED1 g\u1ED1c cho nh\xF3m tu\u1ED5i n\xE0y, ho\u1EB7c \u0111\u01B0a t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng v\u1EC1 0.",
        keys: [cf, volKey]
      });
    else if (trees > 0 && pct === 0 && i >= 2) {
      const [lo, hi] = config.yieldCurve[i] ?? [0, 100];
      add({
        level: "warn",
        code: "A14",
        title: `Nh\xF3m "${band}" c\xF3 c\xE2y nh\u01B0ng t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng = 0`,
        now: `${nf(trees)} g\u1ED1c \xB7 s\u1EA3n l\u01B0\u1EE3ng 0%`,
        rec: `${lo} \u2013 ${hi}%`,
        fix: "Nh\xF3m tu\u1ED5i n\xE0y \u0111\xE3 cho thu ho\u1EA1ch \u2014 h\u1ECFi l\u1EA1i t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng.",
        keys: [volKey]
      });
    }
  });
  const cfUnder3 = parseFloat(answers["cf_under_3"] ?? "") || 0;
  if (c.txt("replant") === "0" && cfUnder3 > 0 && (cy ?? 0) + (cp ?? 0) > 0)
    add({
      level: "warn",
      code: "A15",
      title: "Khai b\xE1o kh\xF4ng t\xE1i canh nh\u01B0ng v\u1EABn c\xF3 c\xE2y d\u01B0\u1EDBi 3 tu\u1ED5i",
      now: `${nf(cfUnder3)} g\u1ED1c d\u01B0\u1EDBi 3 tu\u1ED5i`,
      rec: "Kh\xF4ng t\xE1i canh \u21D2 kh\xF4ng ph\xE1t sinh c\xE2y d\u01B0\u1EDBi 3 tu\u1ED5i",
      fix: "H\u1ECFi l\u1EA1i: c\xE2y non n\xE0y l\xE0 tr\u1ED3ng d\u1EB7m thay c\xE2y ch\u1EBFt hay m\u1EDF r\u1ED9ng di\u1EC7n t\xEDch.",
      keys: ["replant", "cf_under_3"]
    });
  let intercropTotal = 0;
  c.rows("intercrop_tree_exitting_detail").forEach((p) => {
    const id = String(answers[p + "intercrop_tree_exitting_id"] ?? "");
    const sp = config.intercropSpecies[id];
    const nm = answers[p + "intercrop_tree_exitting_name"] || (sp ? sp.name : "c\xE2y tr\u1ED3ng xen");
    const bands = IC_BANDS.map((f) => parseFloat(answers[p + f] ?? "") || 0);
    const trees = bands.reduce((a, b) => a + b, 0);
    const vol = parseFloat(answers[p + "intercrop_tree_exitting_volumn"] ?? "") || 0;
    intercropTotal += trees;
    const firstIcKey = IC_BANDS[0] ?? "";
    if (trees === 0 && vol > 0) {
      add({
        level: "error",
        code: "A16",
        title: `"${nm}" c\xF3 s\u1EA3n l\u01B0\u1EE3ng nh\u01B0ng kh\xF4ng c\xF3 c\xE2y`,
        now: `S\u1EA3n l\u01B0\u1EE3ng ${nf(vol)} kg \xB7 t\u1ED5ng s\u1ED1 c\xE2y 0`,
        rec: "C\xF3 s\u1EA3n l\u01B0\u1EE3ng th\xEC ph\u1EA3i c\xF3 s\u1ED1 c\xE2y",
        fix: "\u0110i\u1EC1n s\u1ED1 c\xE2y theo nh\xF3m tu\u1ED5i, ho\u1EB7c b\u1ECF s\u1EA3n l\u01B0\u1EE3ng.",
        keys: [p + firstIcKey]
      });
      return;
    }
    if (!sp || sp.skip || trees === 0) return;
    if (area !== null && area > 0) {
      const d = trees / area;
      const density = sp.density ?? [0, Infinity];
      if (d > density[1] * 1.3)
        add({
          level: d > density[1] * 2 ? "error" : "warn",
          code: "A16",
          title: `M\u1EADt \u0111\u1ED9 "${sp.name}" v\u01B0\u1EE3t ng\u01B0\u1EE1ng tr\u1ED3ng xen`,
          now: `${nf(d)} c\xE2y/ha`,
          rec: `${density[0]} \u2013 ${density[1]} c\xE2y/ha khi tr\u1ED3ng xen trong v\u01B0\u1EDDn c\xE0 ph\xEA`,
          fix: "Ki\u1EC3m tra l\u1EA1i s\u1ED1 c\xE2y, ho\u1EB7c x\xE1c nh\u1EADn \u0111\xE2y l\xE0 v\u01B0\u1EDDn chuy\xEAn canh lo\xE0i n\xE0y ch\u1EE9 kh\xF4ng ph\u1EA3i tr\u1ED3ng xen.",
          keys: [p + firstIcKey]
        });
    }
    const mature = bands.reduce((a, b, i) => a + ((config.bandMaxAge[i] ?? 0) >= (sp.firstYield ?? 0) ? b : 0), 0);
    if (mature === 0 && vol > 0)
      add({
        level: "warn",
        code: "A16",
        title: `"${sp.name}" ch\u01B0a t\u1EDBi tu\u1ED5i cho qu\u1EA3 nh\u01B0ng \u0111\xE3 c\xF3 s\u1EA3n l\u01B0\u1EE3ng`,
        now: `To\xE0n b\u1ED9 c\xE2y d\u01B0\u1EDBi ${sp.firstYield} tu\u1ED5i \xB7 s\u1EA3n l\u01B0\u1EE3ng ${nf(vol)} kg`,
        rec: `${sp.name} cho qu\u1EA3 t\u1EEB n\u0103m th\u1EE9 ${sp.firstYield}`,
        fix: "Ki\u1EC3m tra l\u1EA1i nh\xF3m tu\u1ED5i c\u1EE7a c\xE2y ho\u1EB7c x\xE1c nh\u1EADn \u0111\xE2y l\xE0 qu\u1EA3 b\xF3i.",
        keys: [p + "intercrop_tree_exitting_volumn"]
      });
    else if (mature > 0 && vol > 0) {
      const per = vol / mature;
      const perTree = sp.perTree ?? [0, Infinity];
      if (per > perTree[1] * 1.3 || per < perTree[0] * 0.7)
        add({
          level: per > perTree[1] * 2 ? "error" : "warn",
          code: "A16",
          title: `S\u1EA3n l\u01B0\u1EE3ng "${sp.name}" tr\xEAn m\u1ED7i c\xE2y ngo\xE0i ng\u01B0\u1EE1ng`,
          now: `${nf(per)} kg/c\xE2y (tr\xEAn ${nf(mature)} c\xE2y \u0111\xE3 t\u1EDBi tu\u1ED5i cho qu\u1EA3)`,
          rec: `${perTree[0]} \u2013 ${perTree[1]} kg/c\xE2y`,
          fix: "X\xE1c nh\u1EADn \u0111\u01A1n v\u1ECB s\u1EA3n l\u01B0\u1EE3ng l\xE0 kg cho c\u1EA3 n\u0103m v\xE0 cho to\xE0n b\u1ED9 s\u1ED1 c\xE2y lo\xE0i n\xE0y.",
          keys: [p + "intercrop_tree_exitting_volumn"]
        });
    }
  });
  const shadeTotal = c.sumRows("shade_tree_exitting_detail", "shade_under_qty");
  if (area !== null && area > 0 && coffeeTrees + intercropTotal + shadeTotal > 0)
    check((coffeeTrees + intercropTotal + shadeTotal) / area, rec.totalDensity, {
      code: "A23",
      title: "T\u1ED5ng m\u1EADt \u0111\u1ED9 c\xE2y tr\xEAn v\u01B0\u1EDDn qu\xE1 d\xE0y",
      fix: "C\u1ED9ng c\u1EA3 c\xE0 ph\xEA, c\xE2y tr\u1ED3ng xen v\xE0 c\xE2y che b\xF3ng \u2014 ki\u1EC3m tra l\u1EA1i nh\xF3m n\xE0o \u0111ang b\u1ECB khai th\u1EEBa.",
      keys: ["coffee_area"]
    });
  if (c.txt("intercrop_tree_yn") === "1" && area !== null && area > 0 && intercropTotal > 0 && intercropTotal / area < 30)
    add({
      level: "warn",
      code: "A24",
      title: "M\u1EADt \u0111\u1ED9 c\xE2y tr\u1ED3ng xen d\u01B0\u1EDBi ng\u01B0\u1EE1ng ghi nh\u1EADn",
      now: `${nf(intercropTotal / area)} c\xE2y/ha`,
      rec: "t\u1EEB 30 c\xE2y/ha tr\u1EDF l\xEAn m\u1EDBi ghi nh\u1EADn l\xE0 tr\u1ED3ng xen",
      fix: "X\xE1c nh\u1EADn l\u1EA1i s\u1ED1 c\xE2y; n\u1EBFu \u0111\xFAng l\xE0 \xEDt h\u01A1n 30 c\xE2y/ha th\xEC kh\xF4ng c\u1EA7n khai \u1EDF ph\u1EA7n n\xE0y.",
      keys: []
    });
  if (area !== null && area > 0 && shadeTotal > 0)
    check(shadeTotal / area, rec.shadeDensity, {
      code: "A25",
      title: "M\u1EADt \u0111\u1ED9 c\xE2y che b\xF3ng ngo\xE0i ng\u01B0\u1EE1ng",
      fix: "Che b\xF3ng qu\xE1 d\xE0y l\xE0m gi\u1EA3m n\u0103ng su\u1EA5t c\xE0 ph\xEA \u2014 x\xE1c nh\u1EADn l\u1EA1i s\u1ED1 c\xE2y \u0111\u1EBFm \u0111\u01B0\u1EE3c.",
      keys: []
    });
  const perimeter = area !== null && area > 0 ? 4 * Math.sqrt(area * 1e4) : 0;
  c.rows("shade_tree_exitting_detail").forEach((p) => {
    const w = parseFloat(answers[p + "hedge_width"] ?? "") || 0;
    const l = parseFloat(answers[p + "hedge_length"] ?? "") || 0;
    const nm = answers[p + "shade_tree_exitting_name"] || "c\xE2y che b\xF3ng";
    if (w > 0)
      check(w, rec.hedgeWidth, {
        code: "A26",
        title: `H\xE0ng r\xE0o "${nm}" qu\xE1 r\u1ED9ng`,
        fix: "Ghi b\u1EC1 r\u1ED9ng d\u1EA3i c\xE2y l\xE0m h\xE0ng r\xE0o, kh\xF4ng ph\u1EA3i kho\u1EA3ng c\xE1ch gi\u1EEFa c\xE1c c\xE2y.",
        keys: [p + "hedge_width"]
      });
    if (l > 0 && perimeter > 0)
      check(l / perimeter * 100, rec.hedgeLenRatio, {
        code: "A27",
        title: `Chi\u1EC1u d\xE0i h\xE0ng r\xE0o "${nm}" v\u01B0\u1EE3t chu vi v\u01B0\u1EDDn`,
        fix: `Chu vi \u01B0\u1EDBc t\xEDnh c\u1EE7a v\u01B0\u1EDDn ${nf(area ?? 0)} ha d\u1EA1ng vu\xF4ng l\xE0 ${nf(perimeter)} m \u2014 h\xE0ng r\xE0o kh\xF4ng th\u1EC3 d\xE0i h\u01A1n nhi\u1EC1u.`,
        keys: [p + "hedge_length"]
      });
    if (w > 0 && l > 0 && area !== null && area > 0)
      check(w * l / 1e4 / area * 100, rec.hedgeAreaPct, {
        code: "A28",
        title: `Di\u1EC7n t\xEDch h\xE0ng r\xE0o "${nm}" chi\u1EBFm qu\xE1 nhi\u1EC1u di\u1EC7n t\xEDch v\u01B0\u1EDDn`,
        fix: `\u0110ang l\xE0 ${nf(w)} m \xD7 ${nf(l)} m = ${nf(w * l / 1e4)} ha. Ki\u1EC3m tra l\u1EA1i \u0111\u01A1n v\u1ECB c\u1EE7a chi\u1EC1u d\xE0i (m\xE9t).`,
        keys: [p + "hedge_width", p + "hedge_length"]
      });
  });
  check(c.num("waste_fruit"), rec.wasteFruit, {
    code: "A29",
    title: "T\u1EF7 l\u1EC7 qu\u1EA3 t\u01B0\u01A1i b\u1ECF \u0111i qu\xE1 cao",
    fix: "Ch\u1EC9 t\xEDnh ph\u1EA7n qu\u1EA3 b\u1ECB lo\u1EA1i b\u1ECF ngo\xE0i v\u01B0\u1EDDn, kh\xF4ng t\xEDnh v\u1ECF qu\u1EA3 sau ch\u1EBF bi\u1EBFn.",
    keys: ["waste_fruit"]
  });
  c.rows("residue_management").forEach((p, i) => {
    const t = parseFloat(answers[p + "residue_practice_total"] ?? "") || 0;
    const name = answers[p + "residue_type_name"] || "d\xF2ng " + (i + 1);
    if (t !== 0 && Math.abs(t - 100) > 0.01)
      add({
        level: "error",
        code: "A30",
        title: `Quy c\xE1ch x\u1EED l\xFD "${name}" ch\u01B0a \u0111\u1EE7 100%`,
        now: `T\u1ED5ng ${nf(t)}%`,
        rec: "\u0110\xFAng 100% (c\xF3 ph\xE1t sinh) ho\u1EB7c 0% (kh\xF4ng ph\xE1t sinh)",
        fix: "Ph\xE2n b\u1ED5 l\u1EA1i ph\u1EA7n tr\u0103m gi\u1EEFa 6 ph\u01B0\u01A1ng th\u1EE9c x\u1EED l\xFD tr\xEAn c\xF9ng m\u1ED9t h\xE0ng.",
        keys: [p + "residue_practice_burn"]
      });
  });
  const fertRows = c.rows("fertilizer_time");
  let inorgTotal = 0;
  let orgTotal = 0;
  let fertTimesCount = 0;
  fertRows.forEach((p, i) => {
    const cat = String(answers[p + "fertilizer_type"] ?? "");
    const total = parseFloat(answers[p + "fertilizer_volumn"] ?? "");
    const per = parseFloat(answers[p + "fertilizer_per_coffee"] ?? "");
    const label = lookupChoice(choices, "onfarm", "fertilizer", answers[p + "fertilizer_name"], "label") || "l\u1EA7n " + (i + 1);
    const organic = cat === "organic";
    if (!isNaN(total) && total > 0) {
      fertTimesCount++;
      if (organic) orgTotal += total;
      else inorgTotal += total;
    }
    if (!isNaN(total) && !isNaN(per) && per > 0 && coffeeTrees > 0) {
      const expect = per * coffeeTrees;
      const diff = (total - expect) / Math.max(expect, 1);
      if (diff > rec.fertMismatch.over)
        add({
          level: "error",
          code: "A37",
          title: `L\u01B0\u1EE3ng ph\xE2n "${label}" kh\xF4ng kh\u1EDBp \u0111\u1ECBnh m\u1EE9c tr\xEAn g\u1ED1c`,
          now: `T\u1ED5ng ${nf(total)} kg \xB7 \u0111\u1ECBnh m\u1EE9c ${nf(per)} kg/g\u1ED1c \xD7 ${nf(coffeeTrees)} g\u1ED1c = ${nf(expect)} kg (cao h\u01A1n ${nf(diff * 100)}%)`,
          rec: `T\u1ED5ng l\u01B0\u1EE3ng b\xF3n x\u1EA5p x\u1EC9 ${nf(expect)} kg, ch\xEAnh l\u1EC7ch d\u01B0\u1EDBi 30%`,
          fix: "M\u1ED9t trong hai \xF4 sai \u0111\u01A1n v\u1ECB ho\u1EB7c th\u1EEBa/thi\u1EBFu s\u1ED1 0 \u2014 ki\u1EC3m tra c\u1EA3 hai tr\u01B0\u1EDBc khi n\u1ED9p phi\u1EBFu.",
          keys: [p + "fertilizer_volumn", p + "fertilizer_per_coffee"]
        });
      else if (diff < -rec.fertMismatch.under)
        add({
          level: "warn",
          code: "A37",
          title: `L\u01B0\u1EE3ng ph\xE2n "${label}" th\u1EA5p h\u01A1n \u0111\u1ECBnh m\u1EE9c tr\xEAn g\u1ED1c`,
          now: `T\u1ED5ng ${nf(total)} kg \xB7 \u01B0\u1EDBc t\xEDnh ${nf(expect)} kg (th\u1EA5p h\u01A1n ${nf(-diff * 100)}%)`,
          rec: `Ch\xEAnh l\u1EC7ch d\u01B0\u1EDBi 30% so v\u1EDBi ${nf(expect)} kg`,
          fix: "H\u1EE3p l\u1EC7 n\u1EBFu ch\u1EC9 b\xF3n cho m\u1ED9t ph\u1EA7n v\u01B0\u1EDDn \u2014 ghi ch\xFA l\u1EA1i ph\u1EA7n di\u1EC7n t\xEDch \u0111\xE3 b\xF3n.",
          keys: [p + "fertilizer_volumn", p + "fertilizer_per_coffee"]
        });
    }
    if (!isNaN(per) && per > 0)
      check(per, organic ? rec.fertOrgTree : rec.fertInorgTree, {
        code: organic ? "A35" : "A34",
        title: `\u0110\u1ECBnh m\u1EE9c ph\xE2n tr\xEAn g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng (${label})`,
        fix: `\u0110\xE2y l\xE0 l\u01B0\u1EE3ng b\xF3n cho m\u1ED9t g\u1ED1c trong m\u1ED9t l\u1EA7n b\xF3n, ph\xE2n ${organic ? "h\u1EEFu c\u01A1" : "v\xF4 c\u01A1"}.`,
        keys: [p + "fertilizer_per_coffee"]
      });
    if (cat === "npk_formula") {
      const npk = (parseFloat(answers[p + "fertilizer_n_nito"] ?? "") || 0) + (parseFloat(answers[p + "fertilizer_p2o5"] ?? "") || 0) + (parseFloat(answers[p + "fertilizer_k2o"] ?? "") || 0);
      if (npk === 0)
        add({
          level: "warn",
          code: "A38",
          title: `Ph\xE2n t\u1EF1 \u1EE7 ch\u01B0a khai h\xE0m l\u01B0\u1EE3ng (${label})`,
          now: "%Nts + %P\u2082O\u2085 + %K\u2082O = 0%",
          rec: recLine(rec.fertNPKSum),
          fix: "Kh\xF4ng c\xF3 h\xE0m l\u01B0\u1EE3ng th\xEC kh\xF4ng t\xEDnh \u0111\u01B0\u1EE3c ph\xE1t th\u1EA3i N\u2082O \u2014 l\u1EA5y s\u1ED1 tr\xEAn bao b\xEC ho\u1EB7c k\u1EBFt qu\u1EA3 ph\xE2n t\xEDch.",
          keys: [p + "fertilizer_n_nito"]
        });
      else
        check(npk, rec.fertNPKSum, {
          code: "A38",
          title: `T\u1ED5ng h\xE0m l\u01B0\u1EE3ng N+P+K b\u1EA5t th\u01B0\u1EDDng (${label})`,
          fix: "C\u1ED9ng ba \xF4 %Nts, %P\u2082O\u2085, %K\u2082O \u2014 \u0111\u1ED1i chi\u1EBFu v\u1EDBi nh\xE3n bao b\xEC.",
          keys: [p + "fertilizer_n_nito", p + "fertilizer_p2o5", p + "fertilizer_k2o"]
        });
    }
    const dt = String(answers[p + "fertilizer_date"] ?? "");
    if (/^\d{4}-\d{2}-\d{2}$/.test(dt) && (dt < "2025-01-01" || dt > "2026-12-31"))
      add({
        level: "warn",
        code: "A39",
        title: `Ng\xE0y b\xF3n n\u1EB1m ngo\xE0i ni\xEAn v\u1EE5 \u0111\xE1nh gi\xE1 (${label})`,
        now: dt,
        rec: "Trong kho\u1EA3ng 01/2025 \u2013 12/2026",
        fix: "Ki\u1EC3m tra l\u1EA1i n\u0103m khi ch\u1ECDn ng\xE0y tr\xEAn thi\u1EBFt b\u1ECB.",
        keys: [p + "fertilizer_date"]
      });
  });
  if (area !== null && area > 0 && inorgTotal > 0)
    check(inorgTotal / area, rec.fertInorgHa, {
      code: "A32",
      title: "L\u01B0\u1EE3ng ph\xE2n v\xF4 c\u01A1 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng",
      fix: `\u0110ang c\u1ED9ng ${nf(inorgTotal)} kg tr\xEAn ${nf(area)} ha t\u1EEB t\u1EA5t c\u1EA3 c\xE1c l\u1EA7n b\xF3n.`,
      keys: ["coffee_area"]
    });
  if (area !== null && area > 0 && orgTotal > 0)
    check(orgTotal / area, rec.fertOrgHa, {
      code: "A33",
      title: "L\u01B0\u1EE3ng ph\xE2n h\u1EEFu c\u01A1 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng",
      fix: `\u0110ang c\u1ED9ng ${nf(orgTotal)} kg tr\xEAn ${nf(area)} ha. N\u1EBFu b\xF3n theo chu k\u1EF3 2\u20133 n\u0103m, chia \u0111\u1EC1u cho s\u1ED1 n\u0103m.`,
      keys: ["coffee_area"]
    });
  if (fertTimesCount > 0)
    check(fertTimesCount, rec.fertTimes, {
      code: "A36",
      title: "S\u1ED1 l\u1EA7n b\xF3n ph\xE2n trong n\u0103m ngo\xE0i ng\u01B0\u1EE1ng",
      fix: "M\u1ED7i d\xF2ng trong b\u1EA3ng l\xE0 m\u1ED9t l\u1EA7n b\xF3n \u2014 g\u1ED9p c\xE1c d\xF2ng tr\xF9ng \u0111\u1EE3t n\u1EBFu c\xF3.",
      keys: []
    });
  const pestRows = c.rows("pesticide_time");
  let pestTimesCount = 0;
  pestRows.forEach((p, i) => {
    if ((parseFloat(answers[p + "pesticide_volumn"] ?? "") || 0) > 0) pestTimesCount++;
    const ai = parseFloat(answers[p + "pesticide_active_ingredient"] ?? "");
    const nm = answers[p + "pesticide_name"] || "l\u1EA7n " + (i + 1);
    if (!isNaN(ai) && ai > 0)
      check(ai, rec.pestActive, {
        code: "A42",
        title: `T\u1EF7 l\u1EC7 ho\u1EA1t ch\u1EA5t b\u1EA5t th\u01B0\u1EDDng (${nm})`,
        fix: 'L\u1EA5y \u0111\xFAng con s\u1ED1 in tr\xEAn nh\xE3n, v\xED d\u1EE5 "Abamectin 3,6%".',
        keys: [p + "pesticide_active_ingredient"]
      });
  });
  if (area !== null && area > 0 && pestRows.length) {
    const v = c.sumRows("pesticide_time", "pesticide_volumn") / area;
    if (v > 0)
      check(v, rec.pestPerHa, {
        code: "A40",
        title: "L\u01B0\u1EE3ng thu\u1ED1c BVTV tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng",
        fix: "Ki\u1EC3m tra \u0111\u01A1n v\u1ECB \u1EDF t\u1EEBng d\xF2ng: l\xEDt v\u1EDBi thu\u1ED1c n\u01B0\u1EDBc, kg v\u1EDBi thu\u1ED1c b\u1ED9t.",
        keys: []
      });
  }
  if (pestTimesCount > 0)
    check(pestTimesCount, rec.pestTimes, {
      code: "A41",
      title: "S\u1ED1 l\u1EA7n s\u1EED d\u1EE5ng thu\u1ED1c BVTV ngo\xE0i ng\u01B0\u1EE1ng",
      fix: "M\u1ED7i d\xF2ng l\xE0 m\u1ED9t l\u1EA7n phun \u2014 x\xE1c nh\u1EADn kh\xF4ng b\u1ECB khai tr\xF9ng.",
      keys: []
    });
  const ec = c.txt("energy_collect");
  const energyRows = c.rows("energy_time");
  let diesel = 0;
  let petrol = 0;
  let elec = 0;
  energyRows.forEach((p) => {
    const t = String(answers[p + "energy_type"] ?? "");
    const u = String(answers[p + "energy_unit"] ?? "");
    let q = parseFloat(answers[p + "energy_volumn"] ?? "") || 0;
    if (u === "2") q *= 1e3;
    if (config.fuelGroups["diesel"]?.includes(t)) diesel += q;
    else if (config.fuelGroups["petrol"]?.includes(t)) petrol += q;
    else if (config.fuelGroups["electric"]?.includes(t) && u === "10") elec += q;
  });
  if (area !== null && area > 0) {
    if (elec > 0)
      check(elec / area, rec.elecPerHa, {
        code: "A43",
        title: "\u0110i\u1EC7n n\u0103ng ti\xEAu th\u1EE5 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng",
        fix: "\u0110\u1ED1i chi\u1EBFu v\u1EDBi ho\xE1 \u0111\u01A1n ti\u1EC1n \u0111i\u1EC7n c\u1EE7a m\xF9a t\u01B0\u1EDBi.",
        keys: []
      });
    if (diesel > 0)
      check(diesel / area, rec.dieselPerHa, {
        code: "A44",
        title: "L\u01B0\u1EE3ng d\u1EA7u diesel tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng",
        fix: "Ki\u1EC3m tra \u0111\u01A1n v\u1ECB: n\u1EBFu khai b\u1EB1ng m\xE9t kh\u1ED1i, 1 m\xB3 = 1.000 l\xEDt.",
        keys: []
      });
    if (petrol > 0)
      check(petrol / area, rec.petrolPerHa, {
        code: "A45",
        title: "L\u01B0\u1EE3ng x\u0103ng tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng",
        fix: "Ch\u1EC9 t\xEDnh x\u0103ng d\xF9ng cho ho\u1EA1t \u0111\u1ED9ng canh t\xE1c, kh\xF4ng t\xEDnh \u0111i l\u1EA1i c\xE1 nh\xE2n.",
        keys: []
      });
  }
  if (ec === "1" && energyRows.length === 0)
    add({
      level: "error",
      code: "A46",
      title: 'Ch\u1ECDn "c\xF3 s\u1ED1 li\u1EC7u n\u0103ng l\u01B0\u1EE3ng" nh\u01B0ng ph\u1EA7n IX \u0111ang tr\u1ED1ng',
      now: "0 d\xF2ng nhi\xEAn li\u1EC7u / n\u0103ng l\u01B0\u1EE3ng",
      rec: '\xCDt nh\u1EA5t m\u1ED9t d\xF2ng khi tr\u1EA3 l\u1EDDi "C\xF3"',
      fix: 'Nh\u1EADp \xEDt nh\u1EA5t m\u1ED9t ngu\u1ED3n n\u0103ng l\u01B0\u1EE3ng, ho\u1EB7c \u0111\u1ED5i c\xE2u tr\u1EA3 l\u1EDDi sang "Kh\xF4ng" \u0111\u1EC3 chuy\u1EC3n sang ph\u1EA7n X\u2013XII.',
      keys: ["energy_collect"]
    });
  if (ec === "0") {
    const has = (n) => c.rows(n).some((p) => Object.keys(answers).some((k) => k.startsWith(p) && String(answers[k]).trim() !== ""));
    if (!has("machine_time") && !has("watering_time") && !has("transport_time"))
      add({
        level: "warn",
        code: "A46",
        title: "Ch\u01B0a c\xF3 d\u1EEF li\u1EC7u thay th\u1EBF cho ph\u1EA7n n\u0103ng l\u01B0\u1EE3ng",
        now: "Ph\u1EA7n X, XI, XII \u0111\u1EC1u tr\u1ED1ng",
        rec: "Ghi nh\u1EADn chi ti\u1EBFt m\xE1y m\xF3c, t\u01B0\u1EDBi n\u01B0\u1EDBc v\xE0 v\u1EADn chuy\u1EC3n",
        fix: "Khi n\xF4ng h\u1ED9 kh\xF4ng cung c\u1EA5p \u0111\u01B0\u1EE3c s\u1ED1 li\u1EC7u n\u0103ng l\u01B0\u1EE3ng, ba ph\u1EA7n n\xE0y l\xE0 ngu\u1ED3n thay th\u1EBF duy nh\u1EA5t \u0111\u1EC3 t\xEDnh ph\xE1t th\u1EA3i.",
        keys: ["energy_collect"]
      });
  }
  c.rows("machine_time").forEach((p) => {
    const n = parseFloat(answers[p + "machine_volume"] ?? "");
    const nm = answers[p + "machine_type_name"] || "m\xE1y";
    if (!isNaN(n) && n > 0)
      check(n, rec.machineTimes, {
        code: "A47",
        title: `S\u1ED1 l\u1EA7n s\u1EED d\u1EE5ng "${nm}" ngo\xE0i ng\u01B0\u1EE1ng`,
        fix: "\u0110\u1EBFm s\u1ED1 l\u1EA7n trong m\u1ED9t n\u0103m cho ri\xEAng lo\u1EA1i m\xE1y n\xE0y.",
        keys: [p + "machine_volume"]
      });
  });
  const wRows = c.rows("watering_time");
  if (wRows.length) {
    const totalWater = c.sumRows("watering_time", "watering_volumn");
    const firstWRow = wRows[0] ?? "";
    if (area !== null && area > 0 && totalWater > 0)
      check(totalWater / area, rec.waterPerHa, {
        code: "A48",
        title: "L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng",
        fix: "Quy \u0111\u1ED5i: 1 m\xB3 = 1.000 l\xEDt. M\u1ED9t \u0111\u1EE3t t\u01B0\u1EDBi g\u1ED1c th\u01B0\u1EDDng 400\u2013600 l\xEDt/g\u1ED1c.",
        keys: [firstWRow + "watering_volumn"]
      });
    if (coffeeTrees > 0 && totalWater > 0)
      check(totalWater / coffeeTrees, rec.waterPerTree, {
        code: "A49",
        title: "L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi tr\xEAn g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng",
        fix: "Nh\xE2n s\u1ED1 l\xEDt m\u1ED7i g\u1ED1c m\u1ED7i \u0111\u1EE3t v\u1EDBi s\u1ED1 \u0111\u1EE3t t\u01B0\u1EDBi trong n\u0103m \u0111\u1EC3 \u0111\u1ED1i chi\u1EBFu.",
        keys: [firstWRow + "watering_volumn"]
      });
    if (area !== null && area > 0) {
      const drip = wRows.filter((p) => String(answers[p + "watering_type"]) === "4").reduce((a, p) => a + (parseFloat(answers[p + "watering_volumn"] ?? "") || 0), 0);
      if (drip > 0)
        check(drip / area, rec.waterDripHa, {
          code: "A50",
          title: "L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi nh\u1ECF gi\u1ECDt cao b\u1EA5t th\u01B0\u1EDDng",
          fix: "Nh\u1ECF gi\u1ECDt ti\u1EBFt ki\u1EC7m 40\u201360% so v\u1EDBi t\u01B0\u1EDBi g\u1ED1c \u2014 x\xE1c nh\u1EADn l\u1EA1i ph\u01B0\u01A1ng ph\xE1p \u0111\xE3 ch\u1ECDn.",
          keys: []
        });
    }
    const areaPct = c.sumRows("watering_time", "watering_area");
    if (areaPct > 0)
      check(areaPct, rec.waterAreaPct, {
        code: "A51",
        title: "T\u1ED5ng % di\u1EC7n t\xEDch t\u01B0\u1EDBi v\u01B0\u1EE3t 100%",
        fix: "Ch\u1EC9 h\u1EE3p l\u1EC7 n\u1EBFu m\u1ED9t khu v\u1EF1c \u0111\u01B0\u1EE3c t\u01B0\u1EDBi b\u1EB1ng nhi\u1EC1u ph\u01B0\u01A1ng ph\xE1p kh\xE1c nhau.",
        keys: [firstWRow + "watering_area"]
      });
  }
  const tRows = c.rows("transport_time");
  if (tRows.length) {
    let inbound = 0;
    let outbound = 0;
    tRows.forEach((p) => {
      const w = parseFloat(answers[p + "transport_weight"] ?? "") || 0;
      const sc = String(answers[p + "transport_area"] ?? "");
      if (sc === "1") inbound += w;
      else if (sc === "3") outbound += w;
      const d = parseFloat(answers[p + "transport_distance"] ?? "");
      if (sc === "2" && !isNaN(d) && d > 0)
        check(d, rec.transportInner, {
          code: "A54",
          title: "Qu\xE3ng \u0111\u01B0\u1EDDng v\u1EADn chuy\u1EC3n trong v\u01B0\u1EDDn qu\xE1 d\xE0i",
          fix: "\u0110\xE2y l\xE0 qu\xE3ng \u0111\u01B0\u1EDDng di chuy\u1EC3n b\xEAn trong v\u01B0\u1EDDn, kh\xF4ng ph\u1EA3i t\u1EEB v\u01B0\u1EDDn ra \u0111i\u1EC3m thu mua.",
          keys: [p + "transport_distance"]
        });
    });
    const inputMass = inorgTotal + orgTotal + c.sumRows("pesticide_time", "pesticide_volumn");
    if (inbound > 0 && inputMass > 0) {
      const diff = Math.abs(inbound - inputMass) / inputMass;
      if (diff > rec.transportDiff.warn)
        add({
          level: "warn",
          code: "A52",
          title: "Kh\u1ED1i l\u01B0\u1EE3ng v\u1EADn chuy\u1EC3n \u0111\u1EA7u v\xE0o l\u1EC7ch so v\u1EDBi v\u1EADt t\u01B0 \u0111\xE3 khai",
          now: `V\u1EADn chuy\u1EC3n \u0111\u1EBFn v\u01B0\u1EDDn ${nf(inbound)} kg \xB7 ph\xE2n b\xF3n + thu\u1ED1c ${nf(inputMass)} kg (l\u1EC7ch ${nf(diff * 100)}%)`,
          rec: "Ch\xEAnh l\u1EC7ch d\u01B0\u1EDBi 30%",
          fix: "H\u1EE3p l\u1EC7 n\u1EBFu c\xF3 v\u1EADn chuy\u1EC3n th\xEAm v\u1EADt t\u01B0 kh\xE1c \u2014 ghi ch\xFA l\u1EA1i lo\u1EA1i v\u1EADt t\u01B0 \u0111\xF3.",
          keys: []
        });
    }
    if (outbound > 0 && harvest !== null && harvest > 0) {
      const diff = Math.abs(outbound - harvest) / harvest;
      if (outbound > harvest * 2)
        add({
          level: "error",
          code: "A53",
          title: "Kh\u1ED1i l\u01B0\u1EE3ng chuy\u1EC3n ra kh\u1ECFi v\u01B0\u1EDDn g\u1EA5p \u0111\xF4i s\u1EA3n l\u01B0\u1EE3ng",
          now: `Chuy\u1EC3n ra ${nf(outbound)} kg \xB7 s\u1EA3n l\u01B0\u1EE3ng ${nf(harvest)} kg`,
          rec: `X\u1EA5p x\u1EC9 ${nf(harvest)} kg, ch\xEAnh l\u1EC7ch d\u01B0\u1EDBi 30%`,
          fix: "Ki\u1EC3m tra l\u1EA1i \u0111\u01A1n v\u1ECB kh\u1ED1i l\u01B0\u1EE3ng v\u1EADn chuy\u1EC3n (kg) v\xE0 s\u1ED1 l\u1EA7n khai tr\xF9ng.",
          keys: ["volumn"]
        });
      else if (diff > rec.transportDiff.warn)
        add({
          level: "warn",
          code: "A53",
          title: "Kh\u1ED1i l\u01B0\u1EE3ng chuy\u1EC3n ra kh\u1ECFi v\u01B0\u1EDDn l\u1EC7ch so v\u1EDBi s\u1EA3n l\u01B0\u1EE3ng",
          now: `Chuy\u1EC3n ra ${nf(outbound)} kg \xB7 s\u1EA3n l\u01B0\u1EE3ng ${nf(harvest)} kg (l\u1EC7ch ${nf(diff * 100)}%)`,
          rec: "Ch\xEAnh l\u1EC7ch d\u01B0\u1EDBi 30%",
          fix: "H\u1EE3p l\u1EC7 n\u1EBFu c\xF3 v\u1EADn chuy\u1EC3n c\u1EA3 ph\u1EE5 ph\u1EA9m ho\u1EB7c n\xF4ng s\u1EA3n tr\u1ED3ng xen.",
          keys: ["volumn"]
        });
    }
  }
  return F;
}

// src/rules/b-rules.ts
function checkRemovalRules(answers, config, choices) {
  const F = [];
  const c = makeAuditContext(answers);
  const { add, check } = makeAdder(F);
  const rec = config.auditRec;
  const declared = c.num("plot_tree_varieties");
  const rows = c.rows("tree_list");
  if (declared !== null && declared > 0)
    check(declared, rec.plotTrees, {
      code: "B1",
      title: "S\u1ED1 g\u1ED1c trong \xF4 m\u1EABu ngo\xE0i ng\u01B0\u1EE1ng",
      fix: `Quy \u0111\u1ED5i ra ${nf(declared / 0.07)} g\u1ED1c/ha. X\xE1c nh\u1EADn \xF4 m\u1EABu \u0111\xFAng 700 m\xB2 v\xE0 \u0111\xE3 \u0111\u1EBFm h\u1EBFt c\xE2y l\xE2u n\u0103m trong \xF4.`,
      keys: ["plot_tree_varieties"]
    });
  if (declared !== null && rows.length && rows.length < declared)
    add({
      level: "warn",
      code: "B1",
      title: "S\u1ED1 d\xF2ng \u0111o \xEDt h\u01A1n s\u1ED1 g\u1ED1c \u0111\xE3 khai",
      now: `Khai ${nf(declared)} g\u1ED1c \xB7 b\u1EA3ng hi\u1EC3n th\u1ECB ${rows.length} d\xF2ng`,
      rec: "S\u1ED1 d\xF2ng \u0111o b\u1EB1ng \u0111\xFAng s\u1ED1 g\u1ED1c \u0111\xE3 khai",
      fix: "Trang ch\u1EC9 hi\u1EC3n th\u1ECB t\u1ED1i \u0111a 60 d\xF2ng \u0111\u1EC3 gi\u1EEF hi\u1EC7u n\u0103ng; tr\xEAn ODK Collect v\u1EABn t\u1EA1o \u0111\u1EE7.",
      keys: ["plot_tree_varieties"]
    });
  let dead = 0;
  const seen = {};
  rows.forEach((p, i) => {
    const type = String(answers[p + "tree_type"] ?? "");
    const health = String(answers[p + "tree_health"] ?? "");
    const dbh = parseFloat(answers[p + "tree_diameter"] ?? "");
    const h = parseFloat(answers[p + "tree_height"] ?? "");
    const age = parseFloat(answers[p + "tree_age"] ?? "");
    const spec = config.treeSpecs[type] ?? config.treeSpecs["_woody"];
    const label = lookupChoice(choices, "removal", "tree_type", type, "label") || spec?.label || "";
    const no = "C\xE2y " + (i + 1) + (label ? ` (${String(label).trim()})` : "");
    if (type === "KH" && String(answers[p + "tree_other_name"] ?? "").trim() === "")
      add({
        level: "error",
        code: "B11",
        title: `${no}: ch\u1ECDn "Kh\xE1c" nh\u01B0ng ch\u01B0a ghi t\xEAn c\xE2y`,
        now: "T\xEAn c\xE2y \u0111\u1EC3 tr\u1ED1ng",
        rec: "Ghi r\xF5 t\xEAn lo\xE0i",
        fix: "Kh\xF4ng x\xE1c \u0111\u1ECBnh \u0111\u01B0\u1EE3c lo\xE0i th\xEC kh\xF4ng tra \u0111\u01B0\u1EE3c h\u1EC7 s\u1ED1 sinh kh\u1ED1i \u0111\u1EC3 t\xEDnh h\u1EA5p th\u1EE5.",
        keys: [p + "tree_other_name"]
      });
    if (health === "3") {
      dead++;
      if (String(answers[p + "tree_die_reason"] ?? "").trim() === "")
        add({
          level: "warn",
          code: "B10",
          title: `${no}: c\xE2y \u0111\xE3 ch\u1EBFt nh\u01B0ng ch\u01B0a ghi nguy\xEAn nh\xE2n`,
          now: "Nguy\xEAn nh\xE2n \u0111\u1EC3 tr\u1ED1ng",
          rec: "Ch\u1ECDn m\u1ED9t nguy\xEAn nh\xE2n trong danh s\xE1ch",
          fix: "Nguy\xEAn nh\xE2n ch\u1EBFt quy\u1EBFt \u0111\u1ECBnh c\xE1ch t\xEDnh ph\u1EA7n carbon \u0111\xE3 m\u1EA5t.",
          keys: [p + "tree_die_reason"]
        });
    }
    if (spec) {
      if (!isNaN(dbh) && dbh > 0)
        check(dbh, spec.dbh, {
          code: type === "CF" ? "B2" : "B3",
          title: `${no}: \u0111\u01B0\u1EDDng k\xEDnh ngang ng\u1EF1c ngo\xE0i ng\u01B0\u1EE1ng`,
          fix: `Khuy\u1EBFn ngh\u1ECB \xE1p d\u1EE5ng cho ${spec.label}. N\u1EBFu \u0111o b\u1EB1ng th\u01B0\u1EDBc d\xE2y, nh\u1EDB chia chu vi cho 3,14.`,
          keys: [p + "tree_diameter"]
        });
      if (!isNaN(h) && h > 0)
        check(h, spec.height, {
          code: type === "CF" ? "B4" : "B5",
          title: `${no}: chi\u1EC1u cao ngo\xE0i ng\u01B0\u1EE1ng`,
          fix: `Khuy\u1EBFn ngh\u1ECB \xE1p d\u1EE5ng cho ${spec.label}. \u0110\u01A1n v\u1ECB l\xE0 centim\xE9t, kh\xF4ng ph\u1EA3i m\xE9t.`,
          keys: [p + "tree_height"]
        });
    }
    if (!isNaN(dbh) && !isNaN(h) && dbh > 0 && h > 0) {
      if (h < dbh)
        add({
          level: "error",
          code: "B6",
          title: `${no}: chi\u1EC1u cao nh\u1ECF h\u01A1n \u0111\u01B0\u1EDDng k\xEDnh`,
          now: `Cao ${nf(h)} cm \xB7 \u0111\u01B0\u1EDDng k\xEDnh ${nf(dbh)} cm`,
          rec: "Chi\u1EC1u cao l\u1EDBn h\u01A1n \u0111\u01B0\u1EDDng k\xEDnh t\u1EEB 20 \u0111\u1EBFn 100 l\u1EA7n",
          fix: "Nhi\u1EC1u kh\u1EA3 n\u0103ng nh\u1EADp nh\u1EA7m c\u1ED9t, ho\u1EB7c ghi chi\u1EC1u cao b\u1EB1ng m\xE9t thay v\xEC centim\xE9t.",
          keys: [p + "tree_height", p + "tree_diameter"]
        });
      else
        check(h / dbh, rec.hdRatio, {
          code: "B6",
          title: `${no}: t\u1EF7 l\u1EC7 chi\u1EC1u cao / \u0111\u01B0\u1EDDng k\xEDnh b\u1EA5t th\u01B0\u1EDDng`,
          fix: "Ki\u1EC3m tra l\u1EA1i c\u1EA3 hai s\u1ED1 \u0111o tr\xEAn c\xF9ng m\u1ED9t c\xE2y.",
          keys: [p + "tree_height", p + "tree_diameter"]
        });
    }
    if (!isNaN(dbh) && !isNaN(age) && age > 0 && dbh > 0)
      check(dbh / age, rec.dbhGrowth, {
        code: "B7",
        title: `${no}: \u0111\u01B0\u1EDDng k\xEDnh kh\xF4ng t\u01B0\u01A1ng x\u1EE9ng v\u1EDBi tu\u1ED5i c\xE2y`,
        fix: `\u0110ang l\xE0 ${nf(dbh)} cm \u1EDF tu\u1ED5i ${nf(age)}. H\u1ECFi l\u1EA1i n\u0103m tr\u1ED3ng ho\u1EB7c \u0111o l\u1EA1i \u0111\u01B0\u1EDDng k\xEDnh.`,
        keys: [p + "tree_diameter", p + "tree_age"]
      });
    if (type === "CF" && !isNaN(age) && age > 0 && spec?.age)
      check(age, spec.age, {
        code: "B8",
        title: `${no}: tu\u1ED5i v\u01B0\u1EE3t chu k\u1EF3 canh t\xE1c th\xF4ng th\u01B0\u1EDDng`,
        fix: "X\xE1c nh\u1EADn l\u1EA1i n\u0103m tr\u1ED3ng \u2014 v\u01B0\u1EDDn qu\xE1 tu\u1ED5i n\xE0y th\u01B0\u1EDDng \u0111\xE3 \u0111\u01B0\u1EE3c t\xE1i canh.",
        keys: [p + "tree_age"]
      });
    const sig = [type, dbh, h, age].join("|");
    if (type && !isNaN(dbh)) seen[sig] = (seen[sig] ?? 0) + 1;
  });
  Object.values(seen).forEach((n) => {
    if (n >= 4)
      add({
        level: "warn",
        code: "B12",
        title: "Nhi\u1EC1u d\xF2ng \u0111o tr\xF9ng nhau ho\xE0n to\xE0n",
        now: `${n} c\xE2y c\xF9ng lo\xE0i, c\xF9ng \u0111\u01B0\u1EDDng k\xEDnh, chi\u1EC1u cao v\xE0 tu\u1ED5i`,
        rec: "M\u1ED7i c\xE2y l\xE0 m\u1ED9t ph\xE9p \u0111o ri\xEAng",
        fix: "Ki\u1EC3m tra xem c\xF3 b\u1ECB sao ch\xE9p d\xF2ng khi nh\u1EADp kh\xF4ng; n\u1EBFu \u0111\xFAng l\xE0 tr\xF9ng th\xEC ghi ch\xFA l\u1EA1i.",
        keys: []
      });
  });
  if (rows.length)
    check(dead / rows.length * 100, rec.deadRate, {
      code: "B9",
      title: "T\u1EF7 l\u1EC7 c\xE2y ch\u1EBFt trong \xF4 m\u1EABu cao",
      fix: `\u0110ang l\xE0 ${dead}/${rows.length} c\xE2y. N\u1EBFu \u0111\xFAng, ghi ch\xFA nguy\xEAn nh\xE2n chung c\u1EE7a c\u1EA3 \xF4.`,
      keys: []
    });
  return F;
}

// src/constants.ts
var INF = Infinity;
var AUDIT_REC = {
  temperature: { rec: [21, 25], warn: [18, 28], err: [10, 35], u: "\xB0C", note: "v\xF9ng c\xE0 ph\xEA T\xE2y Nguy\xEAn" },
  farmArea: { rec: [0.3, 3], warn: [0.1, 10], err: [0, 100], u: "ha", note: "quy m\xF4 n\xF4ng h\u1ED9 trong v\xF9ng d\u1EF1 \xE1n" },
  coffeeDensity: {
    rec: [1110, 1330],
    warn: [700, 1730],
    err: [350, 2660],
    u: "g\u1ED1c/ha",
    note: "3\xD73 m \u0111\u1EBFn 3\xD72,5 m; v\u01B0\u1EDDn tr\u1ED3ng xen 500\u2013800"
  },
  lifeTime: { rec: [20, 25], warn: [15, 32], err: [8, 50], u: "n\u0103m", note: "sau m\u1ED1c n\xE0y v\u01B0\u1EDDn th\u01B0\u1EDDng \u0111\u01B0\u1EE3c t\xE1i canh" },
  pruneStart: { rec: [2, 4], warn: [1, 6], err: [0, INF], u: "tu\u1ED5i", note: "t\u1EA1o h\xECnh t\u1EEB n\u0103m th\u1EE9 2\u20133" },
  prunePercent: { rec: [10, 20], warn: [0, 26], err: [0, 40], u: "%", note: "sinh kh\u1ED1i tr\xEAn m\u1EB7t \u0111\u1EA5t b\u1ECB t\u1EC9a m\u1ED7i n\u0103m" },
  cullYield: { rec: [2, 5], warn: [0, 6.5], err: [0, 10], u: "%/n\u0103m", note: "\u0111\u1ED1n b\u1ECF do n\u0103ng su\u1EA5t k\xE9m" },
  cullPest: { rec: [1, 3], warn: [0, 4], err: [0, 6], u: "%/n\u0103m", note: "\u0111\u1ED1n b\u1ECF do s\xE2u b\u1EC7nh, thi\xEAn tai" },
  cullTotal: { rec: [0, 8], warn: [0, 10.4], err: [0, 100], u: "%/n\u0103m", note: "t\u1ED5ng hai nguy\xEAn nh\xE2n" },
  yieldPerHa: {
    rec: [12e3, 18e3],
    warn: [8400, 23400],
    err: [2400, 36e3],
    u: "kg qu\u1EA3 t\u01B0\u01A1i/ha",
    note: "\u22483\u20134 t\u1EA5n nh\xE2n, quy \u0111\u1ED5i 4,2\u20134,5 kg t\u01B0\u01A1i/kg nh\xE2n"
  },
  yieldPerTree: { rec: [10, 16], warn: [7, 21], err: [2, 32], u: "kg qu\u1EA3 t\u01B0\u01A1i/g\u1ED1c" },
  totalDensity: { rec: [0, 1600], warn: [0, 2080], err: [0, 3200], u: "g\u1ED1c/ha", note: "c\xE0 ph\xEA + tr\u1ED3ng xen + che b\xF3ng" },
  shadeDensity: { rec: [70, 120], warn: [0, 156], err: [0, 240], u: "c\xE2y/ha", note: "mu\u1ED3ng \u0111en 9\xD79 \u0111\u1EBFn 12\xD712 m" },
  hedgeWidth: { rec: [1, 3], warn: [0, 3.9], err: [0, 6], u: "m" },
  hedgeLenRatio: { rec: [0, 100], warn: [0, 130], err: [0, 200], u: "% chu vi v\u01B0\u1EDDn" },
  hedgeAreaPct: { rec: [0, 10], warn: [0, 13], err: [0, 100], u: "% di\u1EC7n t\xEDch v\u01B0\u1EDDn" },
  wasteFruit: { rec: [3, 8], warn: [0, 10.4], err: [0, 20], u: "%", note: "qu\u1EA3 t\u01B0\u01A1i b\u1ECB lo\u1EA1i b\u1ECF" },
  fertInorgHa: { rec: [1500, 2500], warn: [0, 3250], err: [0, 5e3], u: "kg/ha/n\u0103m", note: "ph\xE2n v\xF4 c\u01A1 th\u01B0\u01A1ng ph\u1EA9m" },
  fertOrgHa: {
    rec: [3e3, 7e3],
    warn: [0, 9100],
    err: [0, 14e3],
    u: "kg/ha/n\u0103m",
    note: "ph\xE2n h\u1EEFu c\u01A1, quy v\u1EC1 m\u1EE9c b\xF3n h\u1EB1ng n\u0103m"
  },
  fertInorgTree: { rec: [0.3, 0.8], warn: [0, 1.04], err: [0, 1.6], u: "kg/g\u1ED1c/l\u1EA7n", note: "ph\xE2n v\xF4 c\u01A1" },
  fertOrgTree: { rec: [5, 10], warn: [0, 13], err: [0, 20], u: "kg/g\u1ED1c/l\u1EA7n", note: "ph\xE2n h\u1EEFu c\u01A1" },
  fertTimes: { rec: [3, 5], warn: [0, 6.5], err: [0, 10], u: "l\u1EA7n/n\u0103m" },
  fertNPKSum: { rec: [8, 45], warn: [5, 58], err: [0, 100], u: "%", note: "NPK th\u01B0\u01A1ng ph\u1EA9m th\u01B0\u1EDDng 30\u201345%" },
  fertMismatch: { under: 0.3, over: 0.3 },
  pestPerHa: { rec: [3, 15], warn: [0, 19.5], err: [0, 30], u: "l\xEDt(kg)/ha/n\u0103m" },
  pestTimes: { rec: [2, 6], warn: [0, 8], err: [0, 12], u: "l\u1EA7n/n\u0103m" },
  pestActive: { rec: [5, 80], warn: [5, 80], err: [0, 100], u: "%", note: "theo nh\xE3n s\u1EA3n ph\u1EA9m" },
  elecPerHa: { rec: [400, 900], warn: [0, 1170], err: [0, 1800], u: "kWh/ha/n\u0103m", note: "ch\u1EE7 y\u1EBFu cho b\u01A1m t\u01B0\u1EDBi" },
  dieselPerHa: { rec: [50, 150], warn: [0, 195], err: [0, 300], u: "l\xEDt/ha/n\u0103m" },
  petrolPerHa: { rec: [20, 60], warn: [0, 78], err: [0, 120], u: "l\xEDt/ha/n\u0103m" },
  machineTimes: { rec: [3, 6], warn: [0, 8], err: [0, 15], u: "l\u1EA7n/n\u0103m", note: "cho m\u1ED7i lo\u1EA1i m\xE1y" },
  waterPerHa: { rec: [1500, 2400], warn: [0, 3120], err: [0, 4800], u: "m\xB3/ha/n\u0103m", note: "3\u20134 \u0111\u1EE3t \xD7 400\u2013600 l\xEDt/g\u1ED1c" },
  waterPerTree: { rec: [1.3, 2.2], warn: [0, 2.9], err: [0, 4.4], u: "m\xB3/g\u1ED1c/n\u0103m" },
  waterDripHa: {
    rec: [600, 1400],
    warn: [0, 2e3],
    err: [0, 3200],
    u: "m\xB3/ha/n\u0103m",
    note: "nh\u1ECF gi\u1ECDt d\xF9ng 40\u201360% \u0111\u1ECBnh m\u1EE9c t\u01B0\u1EDBi g\u1ED1c"
  },
  waterAreaPct: { rec: [0, 100], warn: [0, 130], err: [0, 200], u: "%", note: "t\u1ED5ng di\u1EC7n t\xEDch \u0111\u01B0\u1EE3c t\u01B0\u1EDBi" },
  transportDiff: { warn: 0.3 },
  transportInner: { rec: [0, 5], warn: [0, 6.5], err: [0, 50], u: "km", note: "di chuy\u1EC3n b\xEAn trong v\u01B0\u1EDDn" },
  plotTrees: { rec: [70, 120], warn: [49, 156], err: [20, 200], u: "g\u1ED1c/700 m\xB2", note: "\u22481.000\u20131.700 g\u1ED1c/ha" },
  hdRatio: { rec: [20, 100], warn: [15, 130], err: [0, INF], u: "l\u1EA7n", note: "t\u1EF7 l\u1EC7 chi\u1EC1u cao / \u0111\u01B0\u1EDDng k\xEDnh c\u1EE7a c\xE2y th\xE2n g\u1ED7" },
  dbhGrowth: { rec: [0.5, 3], warn: [0, 3.9], err: [0, 6], u: "cm/n\u0103m", note: "0,5\u20131,5 v\u1EDBi c\xE0 ph\xEA; 1,5\u20133 v\u1EDBi c\xE2y g\u1ED7" },
  deadRate: { rec: [0, 5], warn: [0, 6.5], err: [0, 30], u: "%", note: "t\u1EF7 l\u1EC7 c\xE2y ch\u1EBFt trong \xF4 m\u1EABu" }
};
var YIELD_CURVE = [
  [0, 10],
  [40, 70],
  [90, 100],
  [90, 100],
  [70, 90],
  [50, 70],
  [30, 50]
];
var INTERCROP_SPECIES = {
  "1": { name: "B\u01A1", density: [100, 150], perTree: [50, 150], firstYield: 4 },
  "2": { name: "\u0110i\u1EC1u", density: [150, 200], perTree: [5, 15], firstYield: 3 },
  "3": { name: "M\xEDt", density: [150, 250], perTree: [50, 150], firstYield: 3 },
  "4": { name: "S\u1EA7u ri\xEAng", density: [70, 100], perTree: [50, 150], firstYield: 5 },
  "5": { name: "Ti\xEAu", density: [400, 800], perTree: [2, 5], firstYield: 3 },
  "6": { name: "M\u1EAFc ca", density: [100, 150], perTree: [10, 30], firstYield: 5 },
  "7": { name: "Chu\u1ED1i", skip: true }
};
var BAND_MAX_AGE = [2, 5, 10, 15, 20, 25, 99];
var TREE_SPECS = {
  CF: {
    label: "c\xE0 ph\xEA",
    dbh: { rec: [8, 15], warn: [5.6, 19.5], err: [0, 30], u: "cm" },
    height: { rec: [160, 250], warn: [100, 325], err: [0, 500], u: "cm", note: "c\xE0 ph\xEA \u0111\xE3 h\xE3m ng\u1ECDn" },
    age: { rec: [0, 25], warn: [0, 32], err: [0, 50], u: "n\u0103m", note: "t\xE1i canh sau 20\u201325 n\u0103m" }
  },
  TI: {
    label: "tr\u1EE5 ti\xEAu",
    dbh: { rec: [10, 30], warn: [6, 39], err: [0, 60], u: "cm" },
    height: { rec: [300, 900], warn: [200, 1170], err: [0, 1800], u: "cm" }
  },
  _woody: {
    label: "c\xE2y g\u1ED7",
    dbh: { rec: [15, 45], warn: [10, 58], err: [0, 90], u: "cm" },
    height: { rec: [500, 1500], warn: [300, 1950], err: [0, 3e3], u: "cm" }
  }
};
var FUEL_GROUPS = {
  diesel: ["1", "2", "3", "4"],
  petrol: ["6", "7"],
  kerosene: ["5"],
  electric: ["8", "9", "10", "11"]
};
var DEFAULT_AUDIT_CONFIG = {
  auditRec: AUDIT_REC,
  intercropSpecies: INTERCROP_SPECIES,
  treeSpecs: TREE_SPECS,
  yieldCurve: YIELD_CURVE,
  bandMaxAge: BAND_MAX_AGE,
  fuelGroups: FUEL_GROUPS
};

// src/rule-text.ts
var RULE_TEXT_PLACEHOLDERS = ["code", "title", "now", "rec", "fix"];
function expandRuleText(template, finding) {
  return template.replace(
    /\{(\w+)\}/g,
    (whole, name) => RULE_TEXT_PLACEHOLDERS.includes(name) ? String(finding[name] ?? "") : whole
  );
}
function applyRuleText(findings, ruleText) {
  if (!ruleText) return findings;
  return findings.map((f) => {
    const custom = ruleText[f.code];
    if (!custom) return f;
    const title = custom.title?.trim() ? expandRuleText(custom.title, f) : f.title;
    const fix = custom.fix?.trim() ? expandRuleText(custom.fix, f) : f.fix;
    if (title === f.title && fix === f.fix) return f;
    return { ...f, title, fix };
  });
}

// src/rule-default-text.ts
var DYNAMIC_MARK = "\u2026";
var AUDIT_RULE_DEFAULT_TEXT = {
  A1: {
    titles: ["Nhi\u1EC7t \u0111\u1ED9 trung b\xECnh n\u0103m ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["\u0110\u1ED1i chi\u1EBFu v\u1EDBi d\u1EEF li\u1EC7u tr\u1EA1m kh\xED t\u01B0\u1EE3ng g\u1EA7n nh\u1EA5t; kh\xF4ng l\u1EA5y nhi\u1EC7t \u0111\u1ED9 c\u1EE7a m\u1ED9t ng\xE0y c\u1EE5 th\u1EC3."]
  },
  A2: {
    titles: ["Lo\u1EA1i kh\xED h\u1EADu kh\xF4ng t\u1ED3n t\u1EA1i \u1EDF Vi\u1EC7t Nam"],
    fixes: ['V\xF9ng c\xE0 ph\xEA T\xE2y Nguy\xEAn h\u1EA7u h\u1EBFt thu\u1ED9c "Nhi\u1EC7t \u0111\u1EDBi \u1EA9m (m\xF9a kh\xF4 ng\u1EAFn)"; v\xF9ng n\xFAi cao L\xE2m \u0110\u1ED3ng c\xF3 th\u1EC3 l\xE0 "Nhi\u1EC7t \u0111\u1EDBi v\xF9ng n\xFAi".']
  },
  A3: {
    titles: ["Di\u1EC7n t\xEDch canh t\xE1c ngo\xE0i ng\u01B0\u1EE1ng th\u01B0\u1EDDng g\u1EB7p"],
    fixes: ["X\xE1c nh\u1EADn \u0111\u01A1n v\u1ECB l\xE0 h\xE9c-ta, kh\xF4ng ph\u1EA3i s\xE0o hay m\xE9t vu\xF4ng."]
  },
  A4: {
    titles: ["M\u1EADt \u0111\u1ED9 c\xE0 ph\xEA ngo\xE0i ng\u01B0\u1EE1ng canh t\xE1c"],
    fixes: ["\u0110\u1EBFm l\u1EA1i s\u1ED1 g\u1ED1c theo t\u1EEBng nh\xF3m tu\u1ED5i (\u0111ang l\xE0 \u2026 g\u1ED1c) ho\u1EB7c ki\u1EC3m tra l\u1EA1i di\u1EC7n t\xEDch \u2026 ha."]
  },
  A5: {
    titles: ["Chu k\u1EF3 s\u1ED1ng d\u1EF1 ki\u1EBFn ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["H\u1ECFi l\u1EA1i m\u1ED1c n\xF4ng h\u1ED9 d\u1EF1 \u0111\u1ECBnh nh\u1ED5 b\u1ECF \u0111\u1EC3 t\xE1i canh, kh\xF4ng ph\u1EA3i tu\u1ED5i hi\u1EC7n t\u1EA1i c\u1EE7a v\u01B0\u1EDDn."]
  },
  A6: {
    titles: [
      "Tu\u1ED5i b\u1EAFt \u0111\u1EA7u t\u1EC9a c\xE0nh ngo\xE0i ng\u01B0\u1EE1ng",
      "B\u1EAFt \u0111\u1EA7u t\u1EC9a c\xE0nh sau khi c\xE2y \u0111\xE3 h\u1EBFt chu k\u1EF3 s\u1ED1ng"
    ],
    fixes: [
      "Ghi nh\u1EADn n\u0103m b\u1EAFt \u0111\u1EA7u t\u1EA1o h\xECnh/t\u1EC9a \u0111\u1ECBnh k\u1EF3, th\u01B0\u1EDDng ngay sau khi c\xE2y kh\xE9p t\xE1n.",
      "M\u1ED9t trong hai \xF4 b\u1ECB nh\u1EADp nh\u1EA7m \u2014 ki\u1EC3m tra l\u1EA1i c\u1EA3 hai."
    ]
  },
  A7: {
    titles: ["T\u1EF7 l\u1EC7 c\xE0nh t\u1EC9a h\u1EB1ng n\u0103m ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["\u0110\xE2y l\xE0 ph\u1EA7n sinh kh\u1ED1i b\u1ECB c\u1EAFt b\u1ECF m\u1ED7i n\u0103m, kh\xF4ng ph\u1EA3i t\u1EF7 l\u1EC7 c\xE2y \u0111\u01B0\u1EE3c t\u1EC9a."]
  },
  A8: {
    titles: ["T\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF do n\u0103ng su\u1EA5t qu\xE1 cao"],
    fixes: ["N\u1EBFu v\u01B0\u1EDDn \u0111ang t\xE1i canh di\u1EC7n r\u1ED9ng, ghi ch\xFA l\u1EA1i \u0111\u1EC3 ng\u01B0\u1EDDi ki\u1EC3m tra kh\xF4ng coi l\xE0 l\u1ED7i."]
  },
  A9: {
    titles: ["T\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF do s\xE2u b\u1EC7nh/thi\xEAn tai qu\xE1 cao"],
    fixes: ["X\xE1c nh\u1EADn c\xF3 d\u1ECBch b\u1EC7nh ho\u1EB7c thi\xEAn tai trong ni\xEAn v\u1EE5; n\u1EBFu c\xF3, ghi ch\xFA l\u1EA1i."]
  },
  A10: {
    titles: ["T\u1ED5ng t\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF h\u1EB1ng n\u0103m qu\xE1 cao"],
    fixes: ["Hai t\u1EF7 l\u1EC7 n\xE0y c\u1ED9ng l\u1EA1i kh\xF4ng \u0111\u01B0\u1EE3c v\u01B0\u1EE3t 100% s\u1ED1 c\xE2y trong v\u01B0\u1EDDn."]
  },
  A11: {
    titles: ["N\u0103ng su\u1EA5t tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["C\xE2u 13 ghi nh\u1EADn s\u1EA3n l\u01B0\u1EE3ng qu\u1EA3 t\u01B0\u01A1i. N\u1EBFu n\xF4ng h\u1ED9 b\xE1o theo nh\xE2n, nh\xE2n l\u1EA1i v\u1EDBi 4,2\u20134,5."]
  },
  A12: {
    titles: ["N\u0103ng su\u1EA5t tr\xEAn g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Ki\u1EC3m tra ch\xE9o gi\u1EEFa t\u1ED5ng s\u1EA3n l\u01B0\u1EE3ng v\xE0 t\u1ED5ng s\u1ED1 g\u1ED1c \u0111ang khai."]
  },
  A13: {
    titles: [
      'Nh\xF3m "\u2026": t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng l\u1EC7ch \u0111\u01B0\u1EDDng cong n\u0103ng su\u1EA5t',
      "Ngh\u1ECBch sinh l\xFD: c\xE2y d\u01B0\u1EDBi 3 tu\u1ED5i cho s\u1EA3n l\u01B0\u1EE3ng \u2265 c\xE2y 6\u201310 tu\u1ED5i"
    ],
    fixes: [
      "H\u1ECFi l\u1EA1i n\xF4ng h\u1ED9: nh\xF3m tu\u1ED5i n\xE0y cho bao nhi\xEAu ph\u1EA7n tr\u0103m so v\u1EDBi th\u1EDDi k\u1EF3 sung s\u1EE9c nh\u1EA5t c\u1EE7a ch\xEDnh n\xF3.",
      "C\xE0 ph\xEA ch\u1EC9 b\u1EAFt \u0111\u1EA7u cho qu\u1EA3 b\xF3i t\u1EEB n\u0103m th\u1EE9 3. Nhi\u1EC1u kh\u1EA3 n\u0103ng hai \xF4 b\u1ECB nh\u1EADp ng\u01B0\u1EE3c."
    ]
  },
  A14: {
    titles: [
      'Nh\xF3m "\u2026" c\xF3 t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng nh\u01B0ng kh\xF4ng c\xF3 c\xE2y',
      'Nh\xF3m "\u2026" c\xF3 c\xE2y nh\u01B0ng t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng = 0'
    ],
    fixes: [
      "\u0110i\u1EC1n s\u1ED1 g\u1ED1c cho nh\xF3m tu\u1ED5i n\xE0y, ho\u1EB7c \u0111\u01B0a t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng v\u1EC1 0.",
      "Nh\xF3m tu\u1ED5i n\xE0y \u0111\xE3 cho thu ho\u1EA1ch \u2014 h\u1ECFi l\u1EA1i t\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng."
    ]
  },
  A15: {
    titles: ["Khai b\xE1o kh\xF4ng t\xE1i canh nh\u01B0ng v\u1EABn c\xF3 c\xE2y d\u01B0\u1EDBi 3 tu\u1ED5i"],
    fixes: ["H\u1ECFi l\u1EA1i: c\xE2y non n\xE0y l\xE0 tr\u1ED3ng d\u1EB7m thay c\xE2y ch\u1EBFt hay m\u1EDF r\u1ED9ng di\u1EC7n t\xEDch."]
  },
  A16: {
    titles: [
      '"\u2026" c\xF3 s\u1EA3n l\u01B0\u1EE3ng nh\u01B0ng kh\xF4ng c\xF3 c\xE2y',
      'M\u1EADt \u0111\u1ED9 "\u2026" v\u01B0\u1EE3t ng\u01B0\u1EE1ng tr\u1ED3ng xen',
      '"\u2026" ch\u01B0a t\u1EDBi tu\u1ED5i cho qu\u1EA3 nh\u01B0ng \u0111\xE3 c\xF3 s\u1EA3n l\u01B0\u1EE3ng',
      'S\u1EA3n l\u01B0\u1EE3ng "\u2026" tr\xEAn m\u1ED7i c\xE2y ngo\xE0i ng\u01B0\u1EE1ng'
    ],
    fixes: [
      "\u0110i\u1EC1n s\u1ED1 c\xE2y theo nh\xF3m tu\u1ED5i, ho\u1EB7c b\u1ECF s\u1EA3n l\u01B0\u1EE3ng.",
      "Ki\u1EC3m tra l\u1EA1i s\u1ED1 c\xE2y, ho\u1EB7c x\xE1c nh\u1EADn \u0111\xE2y l\xE0 v\u01B0\u1EDDn chuy\xEAn canh lo\xE0i n\xE0y ch\u1EE9 kh\xF4ng ph\u1EA3i tr\u1ED3ng xen.",
      "Ki\u1EC3m tra l\u1EA1i nh\xF3m tu\u1ED5i c\u1EE7a c\xE2y ho\u1EB7c x\xE1c nh\u1EADn \u0111\xE2y l\xE0 qu\u1EA3 b\xF3i.",
      "X\xE1c nh\u1EADn \u0111\u01A1n v\u1ECB s\u1EA3n l\u01B0\u1EE3ng l\xE0 kg cho c\u1EA3 n\u0103m v\xE0 cho to\xE0n b\u1ED9 s\u1ED1 c\xE2y lo\xE0i n\xE0y."
    ]
  },
  A23: {
    titles: ["T\u1ED5ng m\u1EADt \u0111\u1ED9 c\xE2y tr\xEAn v\u01B0\u1EDDn qu\xE1 d\xE0y"],
    fixes: ["C\u1ED9ng c\u1EA3 c\xE0 ph\xEA, c\xE2y tr\u1ED3ng xen v\xE0 c\xE2y che b\xF3ng \u2014 ki\u1EC3m tra l\u1EA1i nh\xF3m n\xE0o \u0111ang b\u1ECB khai th\u1EEBa."]
  },
  A24: {
    titles: ["M\u1EADt \u0111\u1ED9 c\xE2y tr\u1ED3ng xen d\u01B0\u1EDBi ng\u01B0\u1EE1ng ghi nh\u1EADn"],
    fixes: ["X\xE1c nh\u1EADn l\u1EA1i s\u1ED1 c\xE2y; n\u1EBFu \u0111\xFAng l\xE0 \xEDt h\u01A1n 30 c\xE2y/ha th\xEC kh\xF4ng c\u1EA7n khai \u1EDF ph\u1EA7n n\xE0y."]
  },
  A25: {
    titles: ["M\u1EADt \u0111\u1ED9 c\xE2y che b\xF3ng ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Che b\xF3ng qu\xE1 d\xE0y l\xE0m gi\u1EA3m n\u0103ng su\u1EA5t c\xE0 ph\xEA \u2014 x\xE1c nh\u1EADn l\u1EA1i s\u1ED1 c\xE2y \u0111\u1EBFm \u0111\u01B0\u1EE3c."]
  },
  A26: {
    titles: ['H\xE0ng r\xE0o "\u2026" qu\xE1 r\u1ED9ng'],
    fixes: ["Ghi b\u1EC1 r\u1ED9ng d\u1EA3i c\xE2y l\xE0m h\xE0ng r\xE0o, kh\xF4ng ph\u1EA3i kho\u1EA3ng c\xE1ch gi\u1EEFa c\xE1c c\xE2y."]
  },
  A27: {
    titles: ['Chi\u1EC1u d\xE0i h\xE0ng r\xE0o "\u2026" v\u01B0\u1EE3t chu vi v\u01B0\u1EDDn'],
    fixes: ["Chu vi \u01B0\u1EDBc t\xEDnh c\u1EE7a v\u01B0\u1EDDn \u2026 ha d\u1EA1ng vu\xF4ng l\xE0 \u2026 m \u2014 h\xE0ng r\xE0o kh\xF4ng th\u1EC3 d\xE0i h\u01A1n nhi\u1EC1u."]
  },
  A28: {
    titles: ['Di\u1EC7n t\xEDch h\xE0ng r\xE0o "\u2026" chi\u1EBFm qu\xE1 nhi\u1EC1u di\u1EC7n t\xEDch v\u01B0\u1EDDn'],
    fixes: ["\u0110ang l\xE0 \u2026 m \xD7 \u2026 m = \u2026 ha. Ki\u1EC3m tra l\u1EA1i \u0111\u01A1n v\u1ECB c\u1EE7a chi\u1EC1u d\xE0i (m\xE9t)."]
  },
  A29: {
    titles: ["T\u1EF7 l\u1EC7 qu\u1EA3 t\u01B0\u01A1i b\u1ECF \u0111i qu\xE1 cao"],
    fixes: ["Ch\u1EC9 t\xEDnh ph\u1EA7n qu\u1EA3 b\u1ECB lo\u1EA1i b\u1ECF ngo\xE0i v\u01B0\u1EDDn, kh\xF4ng t\xEDnh v\u1ECF qu\u1EA3 sau ch\u1EBF bi\u1EBFn."]
  },
  A30: {
    titles: ['Quy c\xE1ch x\u1EED l\xFD "\u2026" ch\u01B0a \u0111\u1EE7 100%'],
    fixes: ["Ph\xE2n b\u1ED5 l\u1EA1i ph\u1EA7n tr\u0103m gi\u1EEFa 6 ph\u01B0\u01A1ng th\u1EE9c x\u1EED l\xFD tr\xEAn c\xF9ng m\u1ED9t h\xE0ng."]
  },
  A37: {
    titles: [
      'L\u01B0\u1EE3ng ph\xE2n "\u2026" kh\xF4ng kh\u1EDBp \u0111\u1ECBnh m\u1EE9c tr\xEAn g\u1ED1c',
      'L\u01B0\u1EE3ng ph\xE2n "\u2026" th\u1EA5p h\u01A1n \u0111\u1ECBnh m\u1EE9c tr\xEAn g\u1ED1c'
    ],
    fixes: [
      "M\u1ED9t trong hai \xF4 sai \u0111\u01A1n v\u1ECB ho\u1EB7c th\u1EEBa/thi\u1EBFu s\u1ED1 0 \u2014 ki\u1EC3m tra c\u1EA3 hai tr\u01B0\u1EDBc khi n\u1ED9p phi\u1EBFu.",
      "H\u1EE3p l\u1EC7 n\u1EBFu ch\u1EC9 b\xF3n cho m\u1ED9t ph\u1EA7n v\u01B0\u1EDDn \u2014 ghi ch\xFA l\u1EA1i ph\u1EA7n di\u1EC7n t\xEDch \u0111\xE3 b\xF3n."
    ]
  },
  A35: {
    titles: ["\u0110\u1ECBnh m\u1EE9c ph\xE2n tr\xEAn g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng (\u2026)"],
    fixes: ["\u0110\xE2y l\xE0 l\u01B0\u1EE3ng b\xF3n cho m\u1ED9t g\u1ED1c trong m\u1ED9t l\u1EA7n b\xF3n, ph\xE2n \u2026."]
  },
  A34: {
    titles: ["\u0110\u1ECBnh m\u1EE9c ph\xE2n tr\xEAn g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng (\u2026)"],
    fixes: ["\u0110\xE2y l\xE0 l\u01B0\u1EE3ng b\xF3n cho m\u1ED9t g\u1ED1c trong m\u1ED9t l\u1EA7n b\xF3n, ph\xE2n \u2026."]
  },
  A38: {
    titles: [
      "Ph\xE2n t\u1EF1 \u1EE7 ch\u01B0a khai h\xE0m l\u01B0\u1EE3ng (\u2026)",
      "T\u1ED5ng h\xE0m l\u01B0\u1EE3ng N+P+K b\u1EA5t th\u01B0\u1EDDng (\u2026)"
    ],
    fixes: [
      "Kh\xF4ng c\xF3 h\xE0m l\u01B0\u1EE3ng th\xEC kh\xF4ng t\xEDnh \u0111\u01B0\u1EE3c ph\xE1t th\u1EA3i N\u2082O \u2014 l\u1EA5y s\u1ED1 tr\xEAn bao b\xEC ho\u1EB7c k\u1EBFt qu\u1EA3 ph\xE2n t\xEDch.",
      "C\u1ED9ng ba \xF4 %Nts, %P\u2082O\u2085, %K\u2082O \u2014 \u0111\u1ED1i chi\u1EBFu v\u1EDBi nh\xE3n bao b\xEC."
    ]
  },
  A39: {
    titles: ["Ng\xE0y b\xF3n n\u1EB1m ngo\xE0i ni\xEAn v\u1EE5 \u0111\xE1nh gi\xE1 (\u2026)"],
    fixes: ["Ki\u1EC3m tra l\u1EA1i n\u0103m khi ch\u1ECDn ng\xE0y tr\xEAn thi\u1EBFt b\u1ECB."]
  },
  A32: {
    titles: ["L\u01B0\u1EE3ng ph\xE2n v\xF4 c\u01A1 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["\u0110ang c\u1ED9ng \u2026 kg tr\xEAn \u2026 ha t\u1EEB t\u1EA5t c\u1EA3 c\xE1c l\u1EA7n b\xF3n."]
  },
  A33: {
    titles: ["L\u01B0\u1EE3ng ph\xE2n h\u1EEFu c\u01A1 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["\u0110ang c\u1ED9ng \u2026 kg tr\xEAn \u2026 ha. N\u1EBFu b\xF3n theo chu k\u1EF3 2\u20133 n\u0103m, chia \u0111\u1EC1u cho s\u1ED1 n\u0103m."]
  },
  A36: {
    titles: ["S\u1ED1 l\u1EA7n b\xF3n ph\xE2n trong n\u0103m ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["M\u1ED7i d\xF2ng trong b\u1EA3ng l\xE0 m\u1ED9t l\u1EA7n b\xF3n \u2014 g\u1ED9p c\xE1c d\xF2ng tr\xF9ng \u0111\u1EE3t n\u1EBFu c\xF3."]
  },
  A42: {
    titles: ["T\u1EF7 l\u1EC7 ho\u1EA1t ch\u1EA5t b\u1EA5t th\u01B0\u1EDDng (\u2026)"],
    fixes: ['L\u1EA5y \u0111\xFAng con s\u1ED1 in tr\xEAn nh\xE3n, v\xED d\u1EE5 "Abamectin 3,6%".']
  },
  A40: {
    titles: ["L\u01B0\u1EE3ng thu\u1ED1c BVTV tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Ki\u1EC3m tra \u0111\u01A1n v\u1ECB \u1EDF t\u1EEBng d\xF2ng: l\xEDt v\u1EDBi thu\u1ED1c n\u01B0\u1EDBc, kg v\u1EDBi thu\u1ED1c b\u1ED9t."]
  },
  A41: {
    titles: ["S\u1ED1 l\u1EA7n s\u1EED d\u1EE5ng thu\u1ED1c BVTV ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["M\u1ED7i d\xF2ng l\xE0 m\u1ED9t l\u1EA7n phun \u2014 x\xE1c nh\u1EADn kh\xF4ng b\u1ECB khai tr\xF9ng."]
  },
  A43: {
    titles: ["\u0110i\u1EC7n n\u0103ng ti\xEAu th\u1EE5 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["\u0110\u1ED1i chi\u1EBFu v\u1EDBi ho\xE1 \u0111\u01A1n ti\u1EC1n \u0111i\u1EC7n c\u1EE7a m\xF9a t\u01B0\u1EDBi."]
  },
  A44: {
    titles: ["L\u01B0\u1EE3ng d\u1EA7u diesel tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Ki\u1EC3m tra \u0111\u01A1n v\u1ECB: n\u1EBFu khai b\u1EB1ng m\xE9t kh\u1ED1i, 1 m\xB3 = 1.000 l\xEDt."]
  },
  A45: {
    titles: ["L\u01B0\u1EE3ng x\u0103ng tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Ch\u1EC9 t\xEDnh x\u0103ng d\xF9ng cho ho\u1EA1t \u0111\u1ED9ng canh t\xE1c, kh\xF4ng t\xEDnh \u0111i l\u1EA1i c\xE1 nh\xE2n."]
  },
  A46: {
    titles: [
      'Ch\u1ECDn "c\xF3 s\u1ED1 li\u1EC7u n\u0103ng l\u01B0\u1EE3ng" nh\u01B0ng ph\u1EA7n IX \u0111ang tr\u1ED1ng',
      "Ch\u01B0a c\xF3 d\u1EEF li\u1EC7u thay th\u1EBF cho ph\u1EA7n n\u0103ng l\u01B0\u1EE3ng"
    ],
    fixes: [
      'Nh\u1EADp \xEDt nh\u1EA5t m\u1ED9t ngu\u1ED3n n\u0103ng l\u01B0\u1EE3ng, ho\u1EB7c \u0111\u1ED5i c\xE2u tr\u1EA3 l\u1EDDi sang "Kh\xF4ng" \u0111\u1EC3 chuy\u1EC3n sang ph\u1EA7n X\u2013XII.',
      "Khi n\xF4ng h\u1ED9 kh\xF4ng cung c\u1EA5p \u0111\u01B0\u1EE3c s\u1ED1 li\u1EC7u n\u0103ng l\u01B0\u1EE3ng, ba ph\u1EA7n n\xE0y l\xE0 ngu\u1ED3n thay th\u1EBF duy nh\u1EA5t \u0111\u1EC3 t\xEDnh ph\xE1t th\u1EA3i."
    ]
  },
  A47: {
    titles: ['S\u1ED1 l\u1EA7n s\u1EED d\u1EE5ng "\u2026" ngo\xE0i ng\u01B0\u1EE1ng'],
    fixes: ["\u0110\u1EBFm s\u1ED1 l\u1EA7n trong m\u1ED9t n\u0103m cho ri\xEAng lo\u1EA1i m\xE1y n\xE0y."]
  },
  A48: {
    titles: ["L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Quy \u0111\u1ED5i: 1 m\xB3 = 1.000 l\xEDt. M\u1ED9t \u0111\u1EE3t t\u01B0\u1EDBi g\u1ED1c th\u01B0\u1EDDng 400\u2013600 l\xEDt/g\u1ED1c."]
  },
  A49: {
    titles: ["L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi tr\xEAn g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Nh\xE2n s\u1ED1 l\xEDt m\u1ED7i g\u1ED1c m\u1ED7i \u0111\u1EE3t v\u1EDBi s\u1ED1 \u0111\u1EE3t t\u01B0\u1EDBi trong n\u0103m \u0111\u1EC3 \u0111\u1ED1i chi\u1EBFu."]
  },
  A50: {
    titles: ["L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi nh\u1ECF gi\u1ECDt cao b\u1EA5t th\u01B0\u1EDDng"],
    fixes: ["Nh\u1ECF gi\u1ECDt ti\u1EBFt ki\u1EC7m 40\u201360% so v\u1EDBi t\u01B0\u1EDBi g\u1ED1c \u2014 x\xE1c nh\u1EADn l\u1EA1i ph\u01B0\u01A1ng ph\xE1p \u0111\xE3 ch\u1ECDn."]
  },
  A51: {
    titles: ["T\u1ED5ng % di\u1EC7n t\xEDch t\u01B0\u1EDBi v\u01B0\u1EE3t 100%"],
    fixes: ["Ch\u1EC9 h\u1EE3p l\u1EC7 n\u1EBFu m\u1ED9t khu v\u1EF1c \u0111\u01B0\u1EE3c t\u01B0\u1EDBi b\u1EB1ng nhi\u1EC1u ph\u01B0\u01A1ng ph\xE1p kh\xE1c nhau."]
  },
  A54: {
    titles: ["Qu\xE3ng \u0111\u01B0\u1EDDng v\u1EADn chuy\u1EC3n trong v\u01B0\u1EDDn qu\xE1 d\xE0i"],
    fixes: ["\u0110\xE2y l\xE0 qu\xE3ng \u0111\u01B0\u1EDDng di chuy\u1EC3n b\xEAn trong v\u01B0\u1EDDn, kh\xF4ng ph\u1EA3i t\u1EEB v\u01B0\u1EDDn ra \u0111i\u1EC3m thu mua."]
  },
  A52: {
    titles: ["Kh\u1ED1i l\u01B0\u1EE3ng v\u1EADn chuy\u1EC3n \u0111\u1EA7u v\xE0o l\u1EC7ch so v\u1EDBi v\u1EADt t\u01B0 \u0111\xE3 khai"],
    fixes: ["H\u1EE3p l\u1EC7 n\u1EBFu c\xF3 v\u1EADn chuy\u1EC3n th\xEAm v\u1EADt t\u01B0 kh\xE1c \u2014 ghi ch\xFA l\u1EA1i lo\u1EA1i v\u1EADt t\u01B0 \u0111\xF3."]
  },
  A53: {
    titles: [
      "Kh\u1ED1i l\u01B0\u1EE3ng chuy\u1EC3n ra kh\u1ECFi v\u01B0\u1EDDn g\u1EA5p \u0111\xF4i s\u1EA3n l\u01B0\u1EE3ng",
      "Kh\u1ED1i l\u01B0\u1EE3ng chuy\u1EC3n ra kh\u1ECFi v\u01B0\u1EDDn l\u1EC7ch so v\u1EDBi s\u1EA3n l\u01B0\u1EE3ng"
    ],
    fixes: [
      "Ki\u1EC3m tra l\u1EA1i \u0111\u01A1n v\u1ECB kh\u1ED1i l\u01B0\u1EE3ng v\u1EADn chuy\u1EC3n (kg) v\xE0 s\u1ED1 l\u1EA7n khai tr\xF9ng.",
      "H\u1EE3p l\u1EC7 n\u1EBFu c\xF3 v\u1EADn chuy\u1EC3n c\u1EA3 ph\u1EE5 ph\u1EA9m ho\u1EB7c n\xF4ng s\u1EA3n tr\u1ED3ng xen."
    ]
  },
  B1: {
    titles: [
      "S\u1ED1 g\u1ED1c trong \xF4 m\u1EABu ngo\xE0i ng\u01B0\u1EE1ng",
      "S\u1ED1 d\xF2ng \u0111o \xEDt h\u01A1n s\u1ED1 g\u1ED1c \u0111\xE3 khai"
    ],
    fixes: [
      "Quy \u0111\u1ED5i ra \u2026 g\u1ED1c/ha. X\xE1c nh\u1EADn \xF4 m\u1EABu \u0111\xFAng 700 m\xB2 v\xE0 \u0111\xE3 \u0111\u1EBFm h\u1EBFt c\xE2y l\xE2u n\u0103m trong \xF4.",
      "Trang ch\u1EC9 hi\u1EC3n th\u1ECB t\u1ED1i \u0111a 60 d\xF2ng \u0111\u1EC3 gi\u1EEF hi\u1EC7u n\u0103ng; tr\xEAn ODK Collect v\u1EABn t\u1EA1o \u0111\u1EE7."
    ]
  },
  B11: {
    titles: ['\u2026: ch\u1ECDn "Kh\xE1c" nh\u01B0ng ch\u01B0a ghi t\xEAn c\xE2y'],
    fixes: ["Kh\xF4ng x\xE1c \u0111\u1ECBnh \u0111\u01B0\u1EE3c lo\xE0i th\xEC kh\xF4ng tra \u0111\u01B0\u1EE3c h\u1EC7 s\u1ED1 sinh kh\u1ED1i \u0111\u1EC3 t\xEDnh h\u1EA5p th\u1EE5."]
  },
  B10: {
    titles: ["\u2026: c\xE2y \u0111\xE3 ch\u1EBFt nh\u01B0ng ch\u01B0a ghi nguy\xEAn nh\xE2n"],
    fixes: ["Nguy\xEAn nh\xE2n ch\u1EBFt quy\u1EBFt \u0111\u1ECBnh c\xE1ch t\xEDnh ph\u1EA7n carbon \u0111\xE3 m\u1EA5t."]
  },
  B2: {
    titles: ["\u2026: \u0111\u01B0\u1EDDng k\xEDnh ngang ng\u1EF1c ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Khuy\u1EBFn ngh\u1ECB \xE1p d\u1EE5ng cho \u2026. N\u1EBFu \u0111o b\u1EB1ng th\u01B0\u1EDBc d\xE2y, nh\u1EDB chia chu vi cho 3,14."]
  },
  B3: {
    titles: ["\u2026: \u0111\u01B0\u1EDDng k\xEDnh ngang ng\u1EF1c ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Khuy\u1EBFn ngh\u1ECB \xE1p d\u1EE5ng cho \u2026. N\u1EBFu \u0111o b\u1EB1ng th\u01B0\u1EDBc d\xE2y, nh\u1EDB chia chu vi cho 3,14."]
  },
  B4: {
    titles: ["\u2026: chi\u1EC1u cao ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Khuy\u1EBFn ngh\u1ECB \xE1p d\u1EE5ng cho \u2026. \u0110\u01A1n v\u1ECB l\xE0 centim\xE9t, kh\xF4ng ph\u1EA3i m\xE9t."]
  },
  B5: {
    titles: ["\u2026: chi\u1EC1u cao ngo\xE0i ng\u01B0\u1EE1ng"],
    fixes: ["Khuy\u1EBFn ngh\u1ECB \xE1p d\u1EE5ng cho \u2026. \u0110\u01A1n v\u1ECB l\xE0 centim\xE9t, kh\xF4ng ph\u1EA3i m\xE9t."]
  },
  B6: {
    titles: [
      "\u2026: chi\u1EC1u cao nh\u1ECF h\u01A1n \u0111\u01B0\u1EDDng k\xEDnh",
      "\u2026: t\u1EF7 l\u1EC7 chi\u1EC1u cao / \u0111\u01B0\u1EDDng k\xEDnh b\u1EA5t th\u01B0\u1EDDng"
    ],
    fixes: [
      "Nhi\u1EC1u kh\u1EA3 n\u0103ng nh\u1EADp nh\u1EA7m c\u1ED9t, ho\u1EB7c ghi chi\u1EC1u cao b\u1EB1ng m\xE9t thay v\xEC centim\xE9t.",
      "Ki\u1EC3m tra l\u1EA1i c\u1EA3 hai s\u1ED1 \u0111o tr\xEAn c\xF9ng m\u1ED9t c\xE2y."
    ]
  },
  B7: {
    titles: ["\u2026: \u0111\u01B0\u1EDDng k\xEDnh kh\xF4ng t\u01B0\u01A1ng x\u1EE9ng v\u1EDBi tu\u1ED5i c\xE2y"],
    fixes: ["\u0110ang l\xE0 \u2026 cm \u1EDF tu\u1ED5i \u2026. H\u1ECFi l\u1EA1i n\u0103m tr\u1ED3ng ho\u1EB7c \u0111o l\u1EA1i \u0111\u01B0\u1EDDng k\xEDnh."]
  },
  B8: {
    titles: ["\u2026: tu\u1ED5i v\u01B0\u1EE3t chu k\u1EF3 canh t\xE1c th\xF4ng th\u01B0\u1EDDng"],
    fixes: ["X\xE1c nh\u1EADn l\u1EA1i n\u0103m tr\u1ED3ng \u2014 v\u01B0\u1EDDn qu\xE1 tu\u1ED5i n\xE0y th\u01B0\u1EDDng \u0111\xE3 \u0111\u01B0\u1EE3c t\xE1i canh."]
  },
  B12: {
    titles: ["Nhi\u1EC1u d\xF2ng \u0111o tr\xF9ng nhau ho\xE0n to\xE0n"],
    fixes: ["Ki\u1EC3m tra xem c\xF3 b\u1ECB sao ch\xE9p d\xF2ng khi nh\u1EADp kh\xF4ng; n\u1EBFu \u0111\xFAng l\xE0 tr\xF9ng th\xEC ghi ch\xFA l\u1EA1i."]
  },
  B9: {
    titles: ["T\u1EF7 l\u1EC7 c\xE2y ch\u1EBFt trong \xF4 m\u1EABu cao"],
    fixes: ["\u0110ang l\xE0 \u2026/\u2026 c\xE2y. N\u1EBFu \u0111\xFAng, ghi ch\xFA nguy\xEAn nh\xE2n chung c\u1EE7a c\u1EA3 \xF4."]
  }
};

// src/dynamic-lists.ts
var DYNAMIC_LIST_PREFIX = "__";
var MIDDLEMEN_LIST = "__middlemen";
function isDynamicList(listName) {
  return !!listName && listName.startsWith(DYNAMIC_LIST_PREFIX);
}
function selectListOf(type) {
  const m = String(type ?? "").trim().match(/^select_(?:one|multiple)\s+(\S+)/);
  return m?.[1];
}
function dynamicListsOf(survey) {
  const out = /* @__PURE__ */ new Set();
  (survey ?? []).forEach((row) => {
    const list = selectListOf(row.type);
    if (list && isDynamicList(list)) out.add(list);
  });
  return out;
}
function middlemanChoice(mm) {
  return { name: mm.code, label: `${mm.name} (${mm.code})` };
}
var KNOWN_DYNAMIC_LISTS = /* @__PURE__ */ new Set([MIDDLEMEN_LIST]);

// src/form-catalog.ts
var FORM_KEYS = [
  "onfarm",
  "removal",
  "rcp_farm",
  "rcp_biomass",
  "rcp_pump",
  "rcp_soil",
  "rcp_household",
  "rcp_mm"
];
function isFormKey(value) {
  return FORM_KEYS.includes(value);
}
var FORM_SUBJECT = {
  onfarm: { subject: "household", plot: "optional" },
  // Câu hỏi mở #4 (PRD mục 6) chưa chốt — mặc định tạm: removal bắt buộc Vườn.
  removal: { subject: "household", plot: "required" },
  // Phiếu 6 · Phụ lục E — đơn vị quan sát là một lô cà phê. Từ bản 31/10/2026 tách bốn phiếu:
  // canh tác (A–N) và ba phiếu đo đạc hiện trường, cùng gắn Hộ + Vườn.
  rcp_farm: { subject: "household", plot: "required" },
  rcp_biomass: { subject: "household", plot: "required" },
  rcp_pump: { subject: "household", plot: "required" },
  rcp_soil: { subject: "household", plot: "required" },
  // Phiếu 7 · Phụ lục F — cấp hộ, không gắn lô.
  rcp_household: { subject: "household", plot: "forbidden" },
  // Phiếu 8 · Phụ lục G — tham vấn Đại lý/MM.
  rcp_mm: { subject: "middleman", plot: "forbidden" }
};
var FORM_LABELS = {
  onfarm: {
    label: "Ph\u1ECFng v\u1EA5n n\xF4ng h\u1ED9",
    short: "Ph\u1ECFng v\u1EA5n",
    description: "Ho\u1EA1t \u0111\u1ED9ng canh t\xE1c t\u1EA1i h\u1ED9 \u2014 ph\u1EE5c v\u1EE5 t\xEDnh ph\xE1t th\u1EA3i"
  },
  removal: {
    label: "\u0110o \u0111\u1EA1c tr\xEAn v\u01B0\u1EDDn",
    short: "\u0110o \u0111\u1EA1c",
    description: "\u0110o \u0111\u1EBFm sinh kh\u1ED1i tr\xEAn \xF4 m\u1EABu 700m\xB2 \u2014 ph\u1EE5c v\u1EE5 t\xEDnh h\u1EA5p th\u1EE5"
  },
  rcp_farm: {
    label: "RCP \u2014 Kh\u1EA3o s\xE1t l\xF4 c\xE0 ph\xEA (canh t\xE1c)",
    short: "RCP l\xF4",
    description: "Phi\u1EBFu 6 (Ph\u1EE5 l\u1EE5c E, A\u2013N): th\u1EF1c h\xE0nh canh t\xE1c, \u0111\u1EA7u v\xE0o, n\u0103ng su\u1EA5t c\u1EE7a m\u1ED9t l\xF4"
  },
  rcp_biomass: {
    label: "RCP \u2014 \xD4 sinh kh\u1ED1i v\xE0 c\xE2y g\u1ED7",
    short: "RCP sinh kh\u1ED1i",
    description: "Phi\u1EBFu 6 (Ph\u1EE5 l\u1EE5c E, O): \xF4 tr\xF2n 700 m\xB2, c\xE2y g\u1ED7 DBH \u2265 5 cm \u2014 ch\u1EC9 nh\xF3m sinh kh\u1ED1i"
  },
  rcp_pump: {
    label: "RCP \u2014 \u0110o l\u01B0u l\u01B0\u1EE3ng m\xE1y b\u01A1m",
    short: "RCP m\xE1y b\u01A1m",
    description: "Phi\u1EBFu 6 (Ph\u1EE5 l\u1EE5c E, P): ba l\u1EA7n \u0111o l\u01B0u l\u01B0\u1EE3ng, c\u1ED9t \xE1p \u2014 m\u1EABu ph\u1EE5 ki\u1EC3m \u0111\u1ECBnh b\u01A1m"
  },
  rcp_soil: {
    label: "RCP \u2014 L\u1EA5y m\u1EABu \u0111\u1EA5t",
    short: "RCP m\u1EABu \u0111\u1EA5t",
    description: "Phi\u1EBFu 6 (Ph\u1EE5 l\u1EE5c E, Q): \u0111i\u1EC3m m\u1EABu \u0111\u1EA5t l\u1EB7p v\xE0 k\u1EBFt qu\u1EA3 ph\xF2ng th\xED nghi\u1EC7m"
  },
  rcp_household: {
    label: "RCP \u2014 Kh\u1EA3o s\xE1t n\xF4ng h\u1ED9",
    short: "RCP h\u1ED9",
    description: "Phi\u1EBFu 7 (Ph\u1EE5 l\u1EE5c F): sinh k\u1EBF, ti\u1EBFp c\u1EADn th\u1ECB tr\u01B0\u1EDDng, m\u1EA1ng l\u01B0\u1EDBi MM/FO c\u1EE7a h\u1ED9"
  },
  rcp_mm: {
    label: "RCP \u2014 Tham v\u1EA5n MM/FO",
    short: "RCP MM",
    description: "Phi\u1EBFu 8 (Ph\u1EE5 l\u1EE5c G): tham v\u1EA5n \u0111\u1EA1i l\xFD, th\u01B0\u01A1ng l\xE1i, t\u1ED5 ch\u1EE9c n\xF4ng d\xE2n"
  }
};
function formLabel(formKey, variant = "label") {
  return isFormKey(formKey) ? FORM_LABELS[formKey][variant] : formKey;
}

// src/geo.ts
var MAX_BOUNDARY_VERTICES = 500;
var EARTH_RADIUS_M = 6378137;
var toRad = (deg) => deg * Math.PI / 180;
var samePosition = (a, b) => a[0] === b[0] && a[1] === b[1];
function isPosition(p) {
  return Array.isArray(p) && p.length >= 2 && typeof p[0] === "number" && typeof p[1] === "number" && Number.isFinite(p[0]) && Number.isFinite(p[1]);
}
function boundaryVertices(polygon) {
  const ring = polygon?.coordinates?.[0] ?? [];
  if (ring.length > 1 && samePosition(ring[0], ring[ring.length - 1])) return ring.slice(0, -1);
  return ring.slice();
}
function polygonFromVertices(vertices) {
  if (vertices.length < 3) return null;
  const ring = vertices.map((v) => [v[0], v[1]]);
  return { type: "Polygon", coordinates: [[...ring, [ring[0][0], ring[0][1]]]] };
}
function boundaryAreaHa(polygon) {
  const ring = polygon?.coordinates?.[0];
  if (!ring || ring.length < 4) return 0;
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[i + 1];
    sum += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs(sum * EARTH_RADIUS_M * EARTH_RADIUS_M / 2) / 1e4;
}
function segmentsCross(a, b, c, d) {
  const orient = (p, q, r) => {
    const v = (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
    return v > 0 ? 1 : v < 0 ? -1 : 0;
  };
  const onSegment = (p, q, r) => Math.min(p[0], r[0]) <= q[0] && q[0] <= Math.max(p[0], r[0]) && Math.min(p[1], r[1]) <= q[1] && q[1] <= Math.max(p[1], r[1]);
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(a, c, b)) return true;
  if (o2 === 0 && onSegment(a, d, b)) return true;
  if (o3 === 0 && onSegment(c, a, d)) return true;
  if (o4 === 0 && onSegment(c, b, d)) return true;
  return false;
}
function selfIntersects(vertices) {
  const n = vertices.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || i === 0 && j === n - 1) continue;
      if (segmentsCross(a, b, vertices[j], vertices[(j + 1) % n])) return true;
    }
  }
  return false;
}
function boundaryProblem(value) {
  if (value === null || value === void 0) return null;
  const g = value;
  if (typeof g !== "object" || g.type !== "Polygon" || !Array.isArray(g.coordinates)) {
    return "Ranh gi\u1EDBi ph\u1EA3i l\xE0 GeoJSON Polygon";
  }
  if (g.coordinates.length !== 1) return "Ranh gi\u1EDBi ch\u1EC9 c\xF3 m\u1ED9t v\xF2ng ngo\xE0i, kh\xF4ng c\xF3 l\u1ED7";
  const ring = g.coordinates[0];
  if (!Array.isArray(ring) || !ring.every(isPosition)) return "To\u1EA1 \u0111\u1ED9 ranh gi\u1EDBi kh\xF4ng h\u1EE3p l\u1EC7";
  if (ring.length < 4 || !samePosition(ring[0], ring[ring.length - 1])) {
    return "Ranh gi\u1EDBi ph\u1EA3i l\xE0 v\xF2ng kh\xE9p k\xEDn, \xEDt nh\u1EA5t 3 \u0111\u1EC9nh";
  }
  if (ring.some(([lng, lat]) => lng < -180 || lng > 180 || lat < -90 || lat > 90)) {
    return "To\u1EA1 \u0111\u1ED9 ngo\xE0i ph\u1EA1m vi kinh \u0111\u1ED9/v\u0129 \u0111\u1ED9";
  }
  const vertices = boundaryVertices(g);
  if (vertices.length > MAX_BOUNDARY_VERTICES) return `Ranh gi\u1EDBi qu\xE1 nhi\u1EC1u \u0111\u1EC9nh (t\u1ED1i \u0111a ${MAX_BOUNDARY_VERTICES})`;
  const distinct = new Set(vertices.map(([lng, lat]) => `${lng},${lat}`));
  if (distinct.size < 3) return "Ranh gi\u1EDBi c\u1EA7n \xEDt nh\u1EA5t 3 \u0111\u1EC9nh kh\xE1c nhau";
  if (selfIntersects(vertices)) return "C\xE1c c\u1EA1nh ranh gi\u1EDBi c\u1EAFt nhau \u2014 s\u1EEDa l\u1EA1i th\u1EE9 t\u1EF1 \u0111\u1EC9nh";
  if (boundaryAreaHa(g) <= 0) return "Ranh gi\u1EDBi c\xF3 di\u1EC7n t\xEDch b\u1EB1ng 0";
  return null;
}
function describeBoundary(polygon) {
  if (!polygon) return null;
  const ha = boundaryAreaHa(polygon).toLocaleString("vi-VN", { maximumFractionDigits: 2 });
  return `${boundaryVertices(polygon).length} \u0111\u1EC9nh \xB7 ${ha} ha`;
}

// src/completeness.ts
var MAX_REPEAT_INSTANCES = 60;
var AUTO_TYPES = /* @__PURE__ */ new Set(["start", "end", "start-geopoint", "today", "deviceid", "audit"]);
var ATTACHMENT_TYPES = /* @__PURE__ */ new Set(["image", "file", "audio", "video"]);
var REQUIRED_CODE_PREFIX = "REQ";
var CONSTRAINT_CODE_PREFIX = "CONS";
function isCompletenessCode(code) {
  return code.startsWith(`${REQUIRED_CODE_PREFIX}:`) || code.startsWith(`${CONSTRAINT_CODE_PREFIX}:`);
}
function buildSurveyTree(rows) {
  const root = { kind: "root", children: [] };
  const stack = [root];
  (rows ?? []).forEach((row, i) => {
    const type = String(row.type ?? "").trim();
    if (!type) return;
    if (type === "end group" || type === "end repeat") {
      if (stack.length > 1) stack.pop();
      return;
    }
    const kind = type === "begin group" ? "group" : type === "begin repeat" ? "repeat" : "question";
    const node = { ...row, type, kind, idx: i, children: [] };
    stack[stack.length - 1].children.push(node);
    if (kind !== "question") stack.push(node);
  });
  return root;
}
function indexChoices(rows) {
  const map = {};
  (rows ?? []).forEach((row) => {
    const list = String(row["list name"] ?? "");
    if (!list) return;
    (map[list] ??= []).push(row);
  });
  return map;
}
var yes = (v) => String(v ?? "").trim().toLowerCase() === "yes";
function shortLabel(label) {
  const text = String(label ?? "").replace(/\s+/g, " ").trim();
  const withoutNumber = text.replace(/^[\dIVX]+(?:\.\d+)*\.\s*/, "");
  const out = withoutNumber || text;
  return out.length > 160 ? `${out.slice(0, 157)}...` : out;
}
function interpolate(text, answers, prefix) {
  return text.replace(/\$\{\s*([\w- ]+)\s*\}/g, (_m, name) => {
    const v = resolve(answers, name.trim(), prefix);
    return v !== void 0 && v !== "" ? v : name.trim();
  });
}
function rowContext(trail) {
  if (!trail.length) return "";
  return trail.map((t) => `d\xF2ng ${t.index + 1} c\u1EE7a "${t.label}"`).join(" \u203A ");
}
function instanceCount(node, answers, prefix, idx, formKey, choices, opts) {
  const name = String(node.name ?? "");
  const fixed = String(node.ui_fixed_list ?? "").trim();
  if (fixed) return Math.min(MAX_REPEAT_INSTANCES, choices[formKey]?.[fixed]?.length ?? 0);
  const expr = String(node.repeat_count ?? "").trim();
  if (expr) {
    const raw = parseInt(evalExpr(expr, answers, prefix, idx, formKey, choices, opts), 10);
    if (!Number.isFinite(raw)) return 0;
    return Math.max(0, Math.min(MAX_REPEAT_INSTANCES, raw));
  }
  return Math.min(MAX_REPEAT_INSTANCES, countExistingRows(answers, name, prefix));
}
function countExistingRows(answers, repeatName, parentPrefix2 = "") {
  const head = `${parentPrefix2}${repeatName}#`;
  let max = -1;
  for (const key of Object.keys(answers)) {
    if (!key.startsWith(head)) continue;
    const rest = key.slice(head.length);
    const m = /^(\d+)\./.exec(rest);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return max + 1;
}
function isAnswerable(node) {
  const type = String(node.type ?? "").trim();
  if (!type || !node.name) return false;
  if (AUTO_TYPES.has(type) || ATTACHMENT_TYPES.has(type)) return false;
  if (type === "note" || type === "calculate") return false;
  if (/^geo/.test(type)) return false;
  return true;
}
function isPrefilled(node) {
  return String(node.trigger ?? "").trim() !== "";
}
function isComputed(node) {
  if (isPrefilled(node)) return yes(node.readonly);
  return String(node.calculation ?? "").trim() !== "" || yes(node.readonly);
}
var CALC_PASSES = 5;
function maxRowsOf(node) {
  if (String(node.repeat_count ?? "").trim() || String(node.ui_fixed_list ?? "").trim()) return null;
  const n = Number(String(node.ui_max_rows ?? "").trim());
  return Number.isInteger(n) && n > 0 ? n : null;
}
function traverse(tree, answers, formKey, choices, visit, respectRelevant, opts, onRepeat) {
  const lists = choices[formKey];
  const walk = (node, prefix, idx, trail) => {
    if (respectRelevant) {
      const relevant = String(node.relevant ?? "").trim();
      if (relevant && !evalRelevant(relevant, answers, prefix, { ...opts, lists })) return;
    }
    if (node.kind === "group") {
      node.children.forEach((c) => walk(c, prefix, idx, trail));
      return;
    }
    if (node.kind === "repeat") {
      const name = String(node.name ?? "");
      if (!name) return;
      const count = instanceCount(node, answers, prefix, idx, formKey, choices, opts);
      const label = shortLabel(interpolate(String(node.label ?? name), answers, prefix));
      onRepeat?.(node, prefix, count, trail, label);
      for (let i = 0; i < count; i++) {
        node.children.forEach(
          (c) => walk(c, `${prefix}${name}#${i}.`, i, [...trail, { label, index: i }])
        );
      }
      return;
    }
    visit(node, prefix, idx, trail);
  };
  tree.children.forEach((node) => walk(node, "", 0, []));
}
function withCalculations(tree, answers, formKey, choices, opts, blank = /* @__PURE__ */ new Set()) {
  const working = { ...answers };
  for (let pass = 0; pass < CALC_PASSES; pass++) {
    let changed = false;
    traverse(
      tree,
      working,
      formKey,
      choices,
      (node, prefix, idx) => {
        const calc = String(node.calculation ?? "").trim();
        if (!calc || !node.name || isPrefilled(node)) return;
        const key = `${prefix}${node.name}`;
        if (blank.has(key)) {
          working[key] = "";
          return;
        }
        const value = evalExpr(calc, working, prefix, idx, formKey, choices, opts);
        if (working[key] !== value) {
          working[key] = value;
          changed = true;
        }
      },
      false,
      opts
    );
    if (!changed) break;
  }
  return working;
}
function prepare(schema, answers, formKey, opts) {
  const rows = schema?.survey;
  if (!rows?.length) return null;
  const base = opts.choices ?? { [formKey]: indexChoices(schema?.choices) };
  const choices = opts.dynamicLists ? { ...base, [formKey]: { ...base[formKey], ...opts.dynamicLists } } : base;
  const tree = buildSurveyTree(rows);
  const evalOpts = { listOf: listOfQuestions(rows), today: opts.today };
  return { tree, values: relevantValues(tree, answers, formKey, choices, evalOpts), choices, opts: evalOpts };
}
var RELEVANCE_PASSES = 10;
function relevantValues(tree, answers, formKey, choices, opts) {
  const known = questionNames(tree);
  let hidden = /* @__PURE__ */ new Set();
  let values = withCalculations(tree, answers, formKey, choices, opts);
  const all = new Set(Object.keys(values));
  traverse(tree, values, formKey, choices, (node, prefix) => {
    if (node.name) all.add(`${prefix}${node.name}`);
  }, false, opts);
  const candidates = [...all].filter((k) => known.has(baseFieldName(k)));
  for (let pass = 0; pass < RELEVANCE_PASSES; pass++) {
    const keep = /* @__PURE__ */ new Set();
    traverse(
      tree,
      values,
      formKey,
      choices,
      (node, prefix) => {
        if (node.name) keep.add(`${prefix}${node.name}`);
      },
      true,
      opts
    );
    const next = new Set(candidates.filter((k) => !keep.has(k)));
    if (sameSet(next, hidden)) return values;
    hidden = next;
    values = withCalculations(tree, blanked(answers, hidden), formKey, choices, opts, hidden);
  }
  return values;
}
function blanked(answers, keys) {
  const out = { ...answers };
  for (const k of keys) out[k] = "";
  return out;
}
function sameSet(a, b) {
  return a.size === b.size && [...a].every((k) => b.has(k));
}
function countsAsRequired(node) {
  return isAnswerable(node) && yes(node.required) && !isComputed(node);
}
function requiredProgress(schema, answers, formKey, opts = {}) {
  const prepared = prepare(schema, answers, formKey, opts);
  if (!prepared) return { total: 0, done: 0 };
  const { tree, values, choices, opts: evalOpts } = prepared;
  let total = 0;
  let done = 0;
  traverse(
    tree,
    values,
    formKey,
    choices,
    (node, prefix) => {
      if (!countsAsRequired(node)) return;
      total++;
      if (String(values[`${prefix}${node.name}`] ?? "").trim() !== "") done++;
    },
    true,
    evalOpts
  );
  return { total, done };
}
function checkCompleteness(schema, answers, formKey, opts = {}) {
  const prepared = prepare(schema, answers, formKey, opts);
  if (!prepared) return [];
  const { tree, values, choices, opts: evalOpts } = prepared;
  const findings = [];
  traverse(
    tree,
    values,
    formKey,
    choices,
    (node, prefix, _idx, trail) => {
      if (!isAnswerable(node)) return;
      const key = `${prefix}${node.name}`;
      const value = values[key];
      const label = shortLabel(interpolate(String(node.label ?? node.name), values, prefix));
      const where = rowContext(trail);
      const computed = isComputed(node);
      if (value === void 0 || String(value).trim() === "") {
        if (countsAsRequired(node)) {
          findings.push({
            level: "error",
            code: `${REQUIRED_CODE_PREFIX}:${key}`,
            title: `Ch\u01B0a tr\u1EA3 l\u1EDDi c\xE2u b\u1EAFt bu\u1ED9c: ${label}`,
            now: where ? `\u0110ang \u0111\u1EC3 tr\u1ED1ng (${where})` : "\u0110ang \u0111\u1EC3 tr\u1ED1ng",
            rec: String(node.required_message ?? "").trim() || "C\xE2u h\u1ECFi n\xE0y b\u1EAFt bu\u1ED9c ph\u1EA3i c\xF3 c\xE2u tr\u1EA3 l\u1EDDi.",
            fix: "\u0110i\u1EC1n c\xE2u tr\u1EA3 l\u1EDDi cho c\xE2u h\u1ECFi n\xE0y r\u1ED3i n\u1ED9p l\u1EA1i phi\u1EBFu.",
            keys: [key]
          });
        }
        return;
      }
      const list = selectListOf(node.type);
      const known = list && isDynamicList(list) ? opts.dynamicLists?.[list] : void 0;
      if (known) {
        const codes = new Set(known.map((r) => String(r.name)));
        const stray = String(value).trim().split(/\s+/).filter((c) => !codes.has(c));
        if (stray.length) {
          findings.push({
            level: "error",
            code: `${CONSTRAINT_CODE_PREFIX}:${key}`,
            title: `L\u1EF1a ch\u1ECDn kh\xF4ng c\xF3 trong danh m\u1EE5c: ${label}`,
            now: `M\xE3 ${stray.map((c) => `"${c}"`).join(", ")} kh\xF4ng thu\u1ED9c danh m\u1EE5c c\u1EE7a \u0111\u01A1n v\u1ECB${where ? ` (${where})` : ""}`,
            rec: "Ch\u1EC9 ch\u1ECDn m\u1EE5c \u0111ang c\xF3 trong danh m\u1EE5c c\u1EE7a \u0111\u01A1n v\u1ECB \u0111i\u1EC1u tra s\u1EDF h\u1EEFu phi\u1EBFu.",
            fix: "Ch\u1ECDn l\u1EA1i t\u1EEB danh s\xE1ch; n\u1EBFu m\u1EE5c c\u1EA7n ch\u1ECDn ch\u01B0a c\xF3, nh\u1EDD \u0111i\u1EC1u ph\u1ED1i vi\xEAn th\xEAm v\xE0o danh m\u1EE5c.",
            keys: [key]
          });
          return;
        }
      }
      const constraint = String(node.constraint ?? "").trim();
      if (!constraint) return;
      if (computed && Number(value) === 0) return;
      if (checkConstraint(constraint, String(value), {
        ...evalOpts,
        answers: values,
        prefix,
        lists: choices[formKey]
      })) {
        return;
      }
      findings.push({
        level: "error",
        code: `${CONSTRAINT_CODE_PREFIX}:${key}`,
        title: `Gi\xE1 tr\u1ECB kh\xF4ng h\u1EE3p l\u1EC7: ${label}`,
        now: where ? `\u0110ang nh\u1EADp "${value}" (${where})` : `\u0110ang nh\u1EADp "${value}"`,
        rec: String(node.constraint_message ?? "").trim() || `\u0110i\u1EC1u ki\u1EC7n c\u1EE7a c\xE2u h\u1ECFi: ${constraint}`,
        fix: "S\u1EEDa l\u1EA1i gi\xE1 tr\u1ECB cho \u0111\xFAng \u0111i\u1EC1u ki\u1EC7n \u0111\xE3 \u0111\u1EB7t trong b\u1EA3ng h\u1ECFi r\u1ED3i n\u1ED9p l\u1EA1i phi\u1EBFu.",
        keys: [key]
      });
    },
    true,
    evalOpts,
    (node, prefix, count, trail, label) => {
      const max = maxRowsOf(node);
      if (max === null || count <= max) return;
      const key = `${prefix}${node.name}`;
      const where = rowContext(trail);
      findings.push({
        level: "error",
        code: `${CONSTRAINT_CODE_PREFIX}:${key}`,
        title: `Qu\xE1 s\u1ED1 d\xF2ng cho ph\xE9p: ${label}`,
        now: where ? `\u0110ang c\xF3 ${count} d\xF2ng (${where})` : `\u0110ang c\xF3 ${count} d\xF2ng`,
        rec: `T\u1ED1i \u0111a ${max} d\xF2ng.`,
        fix: "Xo\xE1 b\u1EDBt d\xF2ng trong b\u1EA3ng r\u1ED3i n\u1ED9p l\u1EA1i phi\u1EBFu.",
        keys: [key]
      });
    }
  );
  return findings;
}
function questionNames(tree) {
  const names = /* @__PURE__ */ new Set();
  const walk = (node) => {
    if (node.kind === "question" && node.name) names.add(String(node.name));
    node.children.forEach(walk);
  };
  tree.children.forEach(walk);
  return names;
}
function baseFieldName(key) {
  return key.slice(key.lastIndexOf(".") + 1);
}
function pruneIrrelevantAnswers(schema, answers, formKey, opts = {}) {
  const prepared = prepare(schema, answers, formKey, opts);
  if (!prepared) return { answers, removed: [] };
  const { tree, values, choices, opts: evalOpts } = prepared;
  const keep = /* @__PURE__ */ new Set();
  traverse(
    tree,
    values,
    formKey,
    choices,
    (node, prefix) => {
      if (node.name) keep.add(`${prefix}${node.name}`);
    },
    true,
    evalOpts
  );
  const known = questionNames(tree);
  const next = {};
  const removed = [];
  for (const [key, value] of Object.entries(answers)) {
    if (keep.has(key) || !known.has(baseFieldName(key))) next[key] = value;
    else removed.push(key);
  }
  return { answers: next, removed: removed.sort() };
}
function splitLabelNumber(label) {
  const m = String(label ?? "").match(/^\s*([\dIVX]+(?:\.\d+)*)\.\s*(.*)$/s);
  return m ? { num: m[1], text: m[2] } : { num: "", text: String(label ?? "") };
}
function surveySectionId(node) {
  return `sec-${node.name || `g${node.idx}`}`;
}
function surveyColumnLabel(node) {
  if (node.ui_column_label) return String(node.ui_column_label);
  const text = splitLabelNumber(node.label).text.replace(/\$\{[^}]+\}/g, "\u0111\u1ED1i t\u01B0\u1EE3ng").replace(/[?.]+$/, "").trim();
  return text || String(node.name ?? "");
}
function repeatHasNamedRows(node) {
  return /count-selected\s*\(/.test(String(node.repeat_count ?? "")) || !!String(node.ui_fixed_list ?? "").trim() || !!String(node.ui_row_label ?? "").trim();
}
function repeatRowLabel(node, index, parentPrefix2, answers, lists, listOf, evalLabel) {
  const tpl = String(node.ui_row_label ?? "").trim();
  if (tpl) {
    const value = evalLabel(tpl, `${parentPrefix2}${node.name}#${index}.`, index);
    if (value) return value;
  }
  const fixed = String(node.ui_fixed_list ?? "").trim();
  if (fixed) {
    const row = lists[fixed]?.[index];
    if (row) return String(row.label || row.name);
  }
  const count = String(node.repeat_count ?? "");
  if (/count-selected\s*\(/.test(count)) {
    const source = count.match(/\$\{([\w-]+)\}/)?.[1] ?? "";
    const code = String(resolve(answers, source, parentPrefix2) ?? "").trim().split(/\s+/).filter(Boolean)[index];
    if (code) {
      const list = listOf(source);
      const found = list ? lists[list]?.find((r) => String(r.name) === code) : void 0;
      return found ? String(found.label) : code;
    }
  }
  return null;
}
function surveyOutline(schema, answers, formKey, opts = {}) {
  const prepared = prepare(schema, answers, formKey, opts);
  if (!prepared) return [];
  const { tree, values, choices, opts: evalOpts } = prepared;
  const lists = choices[formKey] ?? {};
  const listOf = evalOpts.listOf ?? (() => void 0);
  const isRelevant = (node, prefix) => {
    const relevant = String(node.relevant ?? "").trim();
    return !relevant || evalRelevant(relevant, values, prefix, { ...evalOpts, lists });
  };
  const text = (label, prefix) => interpolate(String(label ?? ""), values, prefix).replace(/\s+/g, " ").trim();
  const question = (node, prefix, applicable) => {
    const key = `${prefix}${node.name}`;
    const value = applicable ? String(values[key] ?? "").trim() : "";
    const list = selectListOf(node.type);
    const rows = list ? lists[list] : void 0;
    const labelOf = (code) => String(rows?.find((r) => String(r.name) === code)?.label ?? code);
    const { num: num3, text: label } = splitLabelNumber(node.label);
    return {
      kind: "question",
      name: String(node.name),
      key,
      num: num3,
      label: text(label, prefix),
      required: countsAsRequired(node),
      applicable,
      value,
      display: !list || !value ? value : value.split(/\s+/).map(labelOf).join(", ")
    };
  };
  const walkChildren = (nodes, prefix, idx) => nodes.flatMap((n) => walk(n, prefix, idx));
  const walk = (node, prefix, idx) => {
    if (!isRelevant(node, prefix)) return [];
    if (node.kind === "group") {
      const items = walkChildren(node.children, prefix, idx);
      if (!items.length) return [];
      const { num: num3, text: label } = splitLabelNumber(node.label);
      return [{ kind: "group", name: String(node.name ?? ""), num: num3, label: text(label, prefix), items }];
    }
    if (node.kind === "repeat") {
      const name = String(node.name ?? "");
      if (!name) return [];
      const answerable = node.children.filter((c) => c.kind === "question" && isAnswerable(c));
      const nested = node.children.filter((c) => c.kind !== "question");
      const count = instanceCount(node, values, prefix, idx, formKey, choices, evalOpts);
      const rows = [];
      for (let i = 0; i < count; i++) {
        const rowPrefix2 = `${prefix}${name}#${i}.`;
        rows.push({
          prefix: rowPrefix2,
          index: i,
          label: repeatRowLabel(
            node,
            i,
            prefix,
            values,
            lists,
            listOf,
            (expr, p, n) => evalExpr(expr, values, p, n, formKey, choices, evalOpts)
          ),
          cells: answerable.map((c) => question(c, rowPrefix2, isRelevant(c, rowPrefix2))),
          items: walkChildren(nested, rowPrefix2, i)
        });
      }
      const { num: num3, text: label } = splitLabelNumber(node.label);
      return [
        {
          kind: "repeat",
          name,
          key: `${prefix}${name}`,
          num: num3,
          label: text(label, prefix),
          columns: answerable.map((c) => ({ name: String(c.name), label: surveyColumnLabel(c), required: countsAsRequired(c) })),
          rows
        }
      ];
    }
    return isAnswerable(node) ? [question(node, prefix, true)] : [];
  };
  const sections = [];
  tree.children.forEach((node) => {
    const items = walk(node, "", 0);
    const [only] = items;
    if (!only) return;
    if (node.kind === "group" && only.kind === "group") {
      sections.push({ id: surveySectionId(node), num: only.num, label: only.label, items: only.items });
      return;
    }
    const last = sections[sections.length - 1];
    if (last && last.id === null) last.items.push(only);
    else sections.push({ id: null, num: "", label: "", items: [only] });
  });
  return sections;
}

// src/master-data.ts
var MASTER_DATA_KEYS = {
  onfarm: ["name", "phone", "code", "coffee_area", "coffee_soil"],
  removal: ["plot_id", "coordinates", "coffee_area"],
  // Ba phiếu RCP (Doc/09a, cột "Khoá chỉ đọc"): tiền tố hh_/plot_/mm_ để không bao giờ
  // trùng tên câu hỏi — phiếu RCP đặt tên câu theo mã mục (a9_date, m0_5_mm...).
  rcp_farm: [
    "hh_farmer_code",
    "hh_province",
    "hh_commune",
    "hh_plot_count",
    "plot_code",
    "plot_gps",
    "plot_area_ha",
    "plot_planting_year",
    "plot_has_polygon"
  ],
  // Ba phiếu đo đạc (bản 31/10/2026) chỉ hiện mã nông hộ và mã mảnh vườn ở mục A.
  rcp_biomass: ["hh_farmer_code", "plot_code"],
  rcp_pump: ["hh_farmer_code", "plot_code"],
  rcp_soil: ["hh_farmer_code", "plot_code"],
  rcp_household: ["hh_farmer_code", "hh_province", "hh_commune", "hh_name", "hh_phone", "hh_plot_count"],
  rcp_mm: ["mm_code", "mm_name", "mm_type", "mm_province", "mm_commune"]
};
function communeWithAddress(commune, address) {
  return [str2(commune), str2(address).trim()].filter(Boolean).join(" \xB7 ");
}
function str2(v) {
  if (v === null || v === void 0) return "";
  return String(v);
}
function masterDataAnswers(formKey, ctx) {
  if (!ctx) return {};
  const { household, plot, middleman } = ctx;
  const areaHa = ctx.snapshotAreaHa ?? plot?.areaHa;
  const gps = plot?.gpsLat != null && plot?.gpsLng != null ? `${str2(plot.gpsLat)} ${str2(plot.gpsLng)}` : "";
  const all = {
    name: str2(household?.fullName),
    phone: str2(household?.phone),
    code: str2(plot?.plotCode),
    plot_id: str2(plot?.plotCode),
    coffee_area: str2(areaHa),
    coffee_soil: str2(plot?.soilType),
    coordinates: gps,
    // Ba phiếu RCP. Phiếu nào chiếu khoá nào thì do `MASTER_DATA_KEYS` quyết.
    hh_name: str2(household?.fullName),
    hh_phone: str2(household?.phone),
    hh_farmer_code: str2(household?.farmerCode),
    hh_province: str2(household?.provinceName),
    hh_commune: communeWithAddress(household?.communeName, household?.addressDetail),
    hh_plot_count: str2(household?.plotCount),
    plot_code: str2(plot?.plotCode),
    plot_gps: gps,
    plot_area_ha: str2(areaHa),
    plot_planting_year: str2(ctx.snapshotPlantingYear ?? plot?.plantingYear),
    // '1'/'0' như câu Có/Không của phiếu — `relevant` đọc `${plot_has_polygon} = '0'`.
    plot_has_polygon: plot ? plot.hasPolygon ? "1" : "0" : "",
    // Mục A và rẽ nhánh Mục I, N của phiếu 8 đọc ba khoá này (bảng ánh xạ Doc/09a).
    mm_code: str2(middleman?.code),
    mm_name: str2(middleman?.name),
    mm_type: str2(middleman?.type),
    mm_province: str2(middleman?.provinceName),
    mm_commune: communeWithAddress(middleman?.communeName, middleman?.addressDetail)
  };
  const wanted = MASTER_DATA_KEYS[formKey] ?? [];
  const out = {};
  for (const key of wanted) {
    const v = all[key];
    if (v !== void 0 && v !== "") out[key] = v;
  }
  return out;
}
function withMasterData(formKey, answers, ctx) {
  return { ...answers, ...masterDataAnswers(formKey, ctx) };
}
function stripMasterDataKeys(formKey, answers) {
  const drop = new Set(MASTER_DATA_KEYS[formKey] ?? []);
  const out = {};
  for (const [k, v] of Object.entries(answers)) {
    if (!drop.has(k)) out[k] = v;
  }
  return out;
}
var SOIL_TYPES = ["\u0110\u1EA5t s\xE9t (m\u1ECBn)", "\u0110\u1EA5t ph\xF9 sa (v\u1EEBa)", "\u0110\u1EA5t c\xE1t (th\xF4)"];
var DEFAULT_SOIL_TYPE = "\u0110\u1EA5t s\xE9t (m\u1ECBn)";
var SOIL_TYPE_INCLUDES = {
  "\u0110\u1EA5t s\xE9t (m\u1ECBn)": "\u0111\u1EA5t s\xE9t c\xE1t, \u0111\u1EA5t s\xE9t ph\xF9 sa, \u0111\u1EA5t bazan, \u0111\u1EA5t s\xE9t",
  "\u0110\u1EA5t ph\xF9 sa (v\u1EEBa)": "\u0111\u1EA5t ph\xF9 sa c\xE1t, \u0111\u1EA5t s\xE9t ph\xF9 sa, \u0111\u1EA5t s\xE9t pha",
  "\u0110\u1EA5t c\xE1t (th\xF4)": "\u0111\u1EA5t c\xE1t, \u0111\u1EA5t c\xE1t pha, \u0111\u1EA5t c\xE1t ph\xF9 sa, \u0111\u1EA5t ph\xF9 sa, \u0111\u1EA5t ph\xF9 sa pha c\xE1t"
};
function isSoilType(value) {
  return typeof value === "string" && SOIL_TYPES.includes(value);
}
var MIDDLEMAN_TYPES = ["coop", "input_dealer", "trader", "company", "nursery", "other"];
var MIDDLEMAN_TYPE_LABELS = {
  coop: "H\u1EE3p t\xE1c x\xE3 / t\u1ED5 nh\xF3m n\xF4ng d\xE2n",
  input_dealer: "\u0110\u1EA1i l\xFD v\u1EADt t\u01B0 (ph\xE2n b\xF3n, thu\u1ED1c BVTV)",
  trader: "Th\u01B0\u01A1ng l\xE1i / \u0111i\u1EC3m thu gom",
  company: "C\xF4ng ty / nh\xE0 xu\u1EA5t kh\u1EA9u",
  nursery: "V\u01B0\u1EDDn \u01B0\u01A1m / c\u01A1 s\u1EDF cung c\u1EA5p c\xE2y gi\u1ED1ng",
  other: "Kh\xE1c"
};
function isMiddlemanType(value) {
  return typeof value === "string" && MIDDLEMAN_TYPES.includes(value);
}
function regionNames(region) {
  let provinceName = "";
  let communeName = "";
  for (let r = region; r; r = r.parent) {
    if (r.level === "commune" && !communeName) communeName = r.name;
    if (r.level === "province") {
      provinceName = r.name;
      break;
    }
  }
  return { provinceName, communeName };
}

// src/catalog.ts
var AUDIT_RULE_CATALOG = [
  // ---- onfarm ------------------------------------------------------------
  { code: "A1", formKey: "onfarm", group: "Kh\xED h\u1EADu & v\xF9ng tr\u1ED3ng", label: "Nhi\u1EC7t \u0111\u1ED9 trung b\xECnh n\u0103m ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A2", formKey: "onfarm", group: "Kh\xED h\u1EADu & v\xF9ng tr\u1ED3ng", label: "Lo\u1EA1i kh\xED h\u1EADu kh\xF4ng t\u1ED3n t\u1EA1i \u1EDF Vi\u1EC7t Nam" },
  { code: "A3", formKey: "onfarm", group: "Di\u1EC7n t\xEDch & m\u1EADt \u0111\u1ED9", label: "Di\u1EC7n t\xEDch canh t\xE1c ngo\xE0i ng\u01B0\u1EE1ng th\u01B0\u1EDDng g\u1EB7p" },
  { code: "A4", formKey: "onfarm", group: "Di\u1EC7n t\xEDch & m\u1EADt \u0111\u1ED9", label: "M\u1EADt \u0111\u1ED9 c\xE0 ph\xEA ngo\xE0i ng\u01B0\u1EE1ng canh t\xE1c" },
  { code: "A5", formKey: "onfarm", group: "Chu k\u1EF3 & t\u1EC9a c\xE0nh", label: "Chu k\u1EF3 s\u1ED1ng d\u1EF1 ki\u1EBFn c\u1EE7a v\u01B0\u1EDDn ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A6", formKey: "onfarm", group: "Chu k\u1EF3 & t\u1EC9a c\xE0nh", label: "Tu\u1ED5i b\u1EAFt \u0111\u1EA7u t\u1EC9a c\xE0nh ngo\xE0i ng\u01B0\u1EE1ng, ho\u1EB7c mu\u1ED9n h\u01A1n c\u1EA3 chu k\u1EF3 s\u1ED1ng" },
  { code: "A7", formKey: "onfarm", group: "Chu k\u1EF3 & t\u1EC9a c\xE0nh", label: "T\u1EF7 l\u1EC7 c\xE0nh t\u1EC9a h\u1EB1ng n\u0103m ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A8", formKey: "onfarm", group: "\u0110\u1ED1n b\u1ECF", label: "T\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF do n\u0103ng su\u1EA5t qu\xE1 cao" },
  { code: "A9", formKey: "onfarm", group: "\u0110\u1ED1n b\u1ECF", label: "T\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF do s\xE2u b\u1EC7nh / thi\xEAn tai qu\xE1 cao" },
  { code: "A10", formKey: "onfarm", group: "\u0110\u1ED1n b\u1ECF", label: "T\u1ED5ng t\u1EF7 l\u1EC7 \u0111\u1ED1n b\u1ECF h\u1EB1ng n\u0103m qu\xE1 cao" },
  { code: "A11", formKey: "onfarm", group: "N\u0103ng su\u1EA5t c\xE0 ph\xEA", label: "N\u0103ng su\u1EA5t tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A12", formKey: "onfarm", group: "N\u0103ng su\u1EA5t c\xE0 ph\xEA", label: "N\u0103ng su\u1EA5t tr\xEAn m\u1ED7i g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A13", formKey: "onfarm", group: "N\u0103ng su\u1EA5t c\xE0 ph\xEA", label: "T\u1EF7 l\u1EC7 s\u1EA3n l\u01B0\u1EE3ng theo nh\xF3m tu\u1ED5i l\u1EC7ch \u0111\u01B0\u1EDDng cong n\u0103ng su\u1EA5t" },
  { code: "A14", formKey: "onfarm", group: "N\u0103ng su\u1EA5t c\xE0 ph\xEA", label: "Nh\xF3m tu\u1ED5i c\xF3 s\u1EA3n l\u01B0\u1EE3ng nh\u01B0ng kh\xF4ng c\xF3 c\xE2y (ho\u1EB7c ng\u01B0\u1EE3c l\u1EA1i)" },
  { code: "A15", formKey: "onfarm", group: "N\u0103ng su\u1EA5t c\xE0 ph\xEA", label: "Khai b\xE1o kh\xF4ng t\xE1i canh nh\u01B0ng v\u1EABn c\xF3 c\xE2y d\u01B0\u1EDBi 3 tu\u1ED5i" },
  { code: "A16", formKey: "onfarm", group: "Tr\u1ED3ng xen", label: "M\u1EADt \u0111\u1ED9 / s\u1EA3n l\u01B0\u1EE3ng / tu\u1ED5i cho qu\u1EA3 c\u1EE7a c\xE2y tr\u1ED3ng xen b\u1EA5t th\u01B0\u1EDDng" },
  { code: "A23", formKey: "onfarm", group: "Tr\u1ED3ng xen", label: "T\u1ED5ng m\u1EADt \u0111\u1ED9 c\xE2y tr\xEAn v\u01B0\u1EDDn qu\xE1 d\xE0y" },
  { code: "A24", formKey: "onfarm", group: "Tr\u1ED3ng xen", label: "M\u1EADt \u0111\u1ED9 c\xE2y tr\u1ED3ng xen d\u01B0\u1EDBi ng\u01B0\u1EE1ng ghi nh\u1EADn" },
  { code: "A25", formKey: "onfarm", group: "C\xE2y che b\xF3ng & h\xE0ng r\xE0o", label: "M\u1EADt \u0111\u1ED9 c\xE2y che b\xF3ng ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A26", formKey: "onfarm", group: "C\xE2y che b\xF3ng & h\xE0ng r\xE0o", label: "H\xE0ng r\xE0o c\xE2y qu\xE1 r\u1ED9ng" },
  { code: "A27", formKey: "onfarm", group: "C\xE2y che b\xF3ng & h\xE0ng r\xE0o", label: "Chi\u1EC1u d\xE0i h\xE0ng r\xE0o v\u01B0\u1EE3t chu vi v\u01B0\u1EDDn" },
  { code: "A28", formKey: "onfarm", group: "C\xE2y che b\xF3ng & h\xE0ng r\xE0o", label: "Di\u1EC7n t\xEDch h\xE0ng r\xE0o chi\u1EBFm qu\xE1 nhi\u1EC1u di\u1EC7n t\xEDch v\u01B0\u1EDDn" },
  { code: "A29", formKey: "onfarm", group: "Ph\u1EE5 ph\u1EA9m", label: "T\u1EF7 l\u1EC7 qu\u1EA3 t\u01B0\u01A1i b\u1ECF \u0111i qu\xE1 cao" },
  { code: "A30", formKey: "onfarm", group: "Ph\u1EE5 ph\u1EA9m", label: "Quy c\xE1ch x\u1EED l\xFD ph\u1EE5 ph\u1EA9m c\u1ED9ng l\u1EA1i ch\u01B0a \u0111\u1EE7 100%" },
  { code: "A32", formKey: "onfarm", group: "Ph\xE2n b\xF3n", label: "L\u01B0\u1EE3ng ph\xE2n v\xF4 c\u01A1 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A33", formKey: "onfarm", group: "Ph\xE2n b\xF3n", label: "L\u01B0\u1EE3ng ph\xE2n h\u1EEFu c\u01A1 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A34", formKey: "onfarm", group: "Ph\xE2n b\xF3n", label: "\u0110\u1ECBnh m\u1EE9c ph\xE2n v\xF4 c\u01A1 tr\xEAn m\u1ED7i g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A35", formKey: "onfarm", group: "Ph\xE2n b\xF3n", label: "\u0110\u1ECBnh m\u1EE9c ph\xE2n h\u1EEFu c\u01A1 tr\xEAn m\u1ED7i g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A36", formKey: "onfarm", group: "Ph\xE2n b\xF3n", label: "S\u1ED1 l\u1EA7n b\xF3n ph\xE2n trong n\u0103m ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A37", formKey: "onfarm", group: "Ph\xE2n b\xF3n", label: "L\u01B0\u1EE3ng ph\xE2n t\u1EEBng l\u1EA7n kh\xF4ng kh\u1EDBp \u0111\u1ECBnh m\u1EE9c tr\xEAn g\u1ED1c" },
  { code: "A38", formKey: "onfarm", group: "Ph\xE2n b\xF3n", label: "Ph\xE2n t\u1EF1 \u1EE7 ch\u01B0a khai h\xE0m l\u01B0\u1EE3ng, ho\u1EB7c t\u1ED5ng N+P+K b\u1EA5t th\u01B0\u1EDDng" },
  { code: "A39", formKey: "onfarm", group: "Ph\xE2n b\xF3n", label: "Ng\xE0y b\xF3n n\u1EB1m ngo\xE0i ni\xEAn v\u1EE5 \u0111ang \u0111\xE1nh gi\xE1" },
  { code: "A40", formKey: "onfarm", group: "Thu\u1ED1c BVTV", label: "L\u01B0\u1EE3ng thu\u1ED1c BVTV tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A41", formKey: "onfarm", group: "Thu\u1ED1c BVTV", label: "S\u1ED1 l\u1EA7n s\u1EED d\u1EE5ng thu\u1ED1c BVTV ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A42", formKey: "onfarm", group: "Thu\u1ED1c BVTV", label: "T\u1EF7 l\u1EC7 ho\u1EA1t ch\u1EA5t trong thu\u1ED1c b\u1EA5t th\u01B0\u1EDDng" },
  { code: "A43", formKey: "onfarm", group: "N\u0103ng l\u01B0\u1EE3ng & m\xE1y m\xF3c", label: "\u0110i\u1EC7n n\u0103ng ti\xEAu th\u1EE5 tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A44", formKey: "onfarm", group: "N\u0103ng l\u01B0\u1EE3ng & m\xE1y m\xF3c", label: "L\u01B0\u1EE3ng d\u1EA7u diesel tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A45", formKey: "onfarm", group: "N\u0103ng l\u01B0\u1EE3ng & m\xE1y m\xF3c", label: "L\u01B0\u1EE3ng x\u0103ng tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A46", formKey: "onfarm", group: "N\u0103ng l\u01B0\u1EE3ng & m\xE1y m\xF3c", label: "Khai c\xF3 s\u1ED1 li\u1EC7u n\u0103ng l\u01B0\u1EE3ng nh\u01B0ng ph\u1EA7n n\u0103ng l\u01B0\u1EE3ng b\u1ECF tr\u1ED1ng" },
  { code: "A47", formKey: "onfarm", group: "N\u0103ng l\u01B0\u1EE3ng & m\xE1y m\xF3c", label: "S\u1ED1 l\u1EA7n s\u1EED d\u1EE5ng m\xE1y m\xF3c ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A48", formKey: "onfarm", group: "T\u01B0\u1EDBi n\u01B0\u1EDBc", label: "L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi tr\xEAn h\xE9c-ta ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A49", formKey: "onfarm", group: "T\u01B0\u1EDBi n\u01B0\u1EDBc", label: "L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi tr\xEAn m\u1ED7i g\u1ED1c ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "A50", formKey: "onfarm", group: "T\u01B0\u1EDBi n\u01B0\u1EDBc", label: "L\u01B0\u1EE3ng n\u01B0\u1EDBc t\u01B0\u1EDBi nh\u1ECF gi\u1ECDt cao b\u1EA5t th\u01B0\u1EDDng" },
  { code: "A51", formKey: "onfarm", group: "T\u01B0\u1EDBi n\u01B0\u1EDBc", label: "T\u1ED5ng % di\u1EC7n t\xEDch t\u01B0\u1EDBi v\u01B0\u1EE3t 100%" },
  { code: "A52", formKey: "onfarm", group: "V\u1EADn chuy\u1EC3n", label: "Kh\u1ED1i l\u01B0\u1EE3ng v\u1EADn chuy\u1EC3n \u0111\u1EA7u v\xE0o l\u1EC7ch so v\u1EDBi v\u1EADt t\u01B0 \u0111\xE3 khai" },
  { code: "A53", formKey: "onfarm", group: "V\u1EADn chuy\u1EC3n", label: "Kh\u1ED1i l\u01B0\u1EE3ng chuy\u1EC3n ra kh\u1ECFi v\u01B0\u1EDDn l\u1EC7ch so v\u1EDBi s\u1EA3n l\u01B0\u1EE3ng" },
  { code: "A54", formKey: "onfarm", group: "V\u1EADn chuy\u1EC3n", label: "Qu\xE3ng \u0111\u01B0\u1EDDng v\u1EADn chuy\u1EC3n trong v\u01B0\u1EDDn qu\xE1 d\xE0i" },
  // ---- removal -----------------------------------------------------------
  { code: "B1", formKey: "removal", group: "\xD4 m\u1EABu", label: "S\u1ED1 g\u1ED1c trong \xF4 m\u1EABu ngo\xE0i ng\u01B0\u1EE1ng, ho\u1EB7c \u0111o thi\u1EBFu d\xF2ng so v\u1EDBi s\u1ED1 \u0111\xE3 khai" },
  { code: "B9", formKey: "removal", group: "\xD4 m\u1EABu", label: "T\u1EF7 l\u1EC7 c\xE2y ch\u1EBFt trong \xF4 m\u1EABu cao" },
  { code: "B12", formKey: "removal", group: "\xD4 m\u1EABu", label: "Nhi\u1EC1u d\xF2ng \u0111o tr\xF9ng nhau ho\xE0n to\xE0n" },
  { code: "B2", formKey: "removal", group: "K\xEDch th\u01B0\u1EDBc c\xE2y", label: "\u0110\u01B0\u1EDDng k\xEDnh ngang ng\u1EF1c c\xE2y c\xE0 ph\xEA ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "B3", formKey: "removal", group: "K\xEDch th\u01B0\u1EDBc c\xE2y", label: "\u0110\u01B0\u1EDDng k\xEDnh ngang ng\u1EF1c c\xE2y tr\u1ED3ng xen ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "B4", formKey: "removal", group: "K\xEDch th\u01B0\u1EDBc c\xE2y", label: "Chi\u1EC1u cao c\xE2y c\xE0 ph\xEA ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "B5", formKey: "removal", group: "K\xEDch th\u01B0\u1EDBc c\xE2y", label: "Chi\u1EC1u cao c\xE2y tr\u1ED3ng xen ngo\xE0i ng\u01B0\u1EE1ng" },
  { code: "B6", formKey: "removal", group: "T\u01B0\u01A1ng quan k\xEDch th\u01B0\u1EDBc", label: "T\u1EF7 l\u1EC7 chi\u1EC1u cao / \u0111\u01B0\u1EDDng k\xEDnh b\u1EA5t th\u01B0\u1EDDng" },
  { code: "B7", formKey: "removal", group: "T\u01B0\u01A1ng quan k\xEDch th\u01B0\u1EDBc", label: "\u0110\u01B0\u1EDDng k\xEDnh kh\xF4ng t\u01B0\u01A1ng x\u1EE9ng v\u1EDBi tu\u1ED5i c\xE2y" },
  { code: "B8", formKey: "removal", group: "T\u01B0\u01A1ng quan k\xEDch th\u01B0\u1EDBc", label: "Tu\u1ED5i c\xE2y v\u01B0\u1EE3t chu k\u1EF3 canh t\xE1c th\xF4ng th\u01B0\u1EDDng" },
  { code: "B10", formKey: "removal", group: "Ghi ch\xE9p t\u1EA1i hi\u1EC7n tr\u01B0\u1EDDng", label: "C\xE2y \u0111\xE3 ch\u1EBFt nh\u01B0ng ch\u01B0a ghi nguy\xEAn nh\xE2n" },
  { code: "B11", formKey: "removal", group: "Ghi ch\xE9p t\u1EA1i hi\u1EC7n tr\u01B0\u1EDDng", label: 'Ch\u1ECDn "Kh\xE1c" nh\u01B0ng ch\u01B0a ghi t\xEAn c\xE2y' }
];
var AUDIT_RULE_BY_CODE = Object.fromEntries(
  AUDIT_RULE_CATALOG.map((r) => [r.code, r])
);
function catalogFor(formKey) {
  return AUDIT_RULE_CATALOG.filter((r) => r.formKey === formKey);
}
var AUDIT_REC_FORM_KEY = {
  // ---- onfarm (luật A) ---------------------------------------------------
  temperature: "onfarm",
  farmArea: "onfarm",
  coffeeDensity: "onfarm",
  lifeTime: "onfarm",
  pruneStart: "onfarm",
  prunePercent: "onfarm",
  cullYield: "onfarm",
  cullPest: "onfarm",
  cullTotal: "onfarm",
  yieldPerHa: "onfarm",
  yieldPerTree: "onfarm",
  totalDensity: "onfarm",
  shadeDensity: "onfarm",
  hedgeWidth: "onfarm",
  hedgeLenRatio: "onfarm",
  hedgeAreaPct: "onfarm",
  wasteFruit: "onfarm",
  fertInorgHa: "onfarm",
  fertOrgHa: "onfarm",
  fertInorgTree: "onfarm",
  fertOrgTree: "onfarm",
  fertTimes: "onfarm",
  fertNPKSum: "onfarm",
  fertMismatch: "onfarm",
  pestPerHa: "onfarm",
  pestTimes: "onfarm",
  pestActive: "onfarm",
  elecPerHa: "onfarm",
  dieselPerHa: "onfarm",
  petrolPerHa: "onfarm",
  machineTimes: "onfarm",
  waterPerHa: "onfarm",
  waterPerTree: "onfarm",
  waterDripHa: "onfarm",
  waterAreaPct: "onfarm",
  transportDiff: "onfarm",
  transportInner: "onfarm",
  // ---- removal (luật B) --------------------------------------------------
  plotTrees: "removal",
  hdRatio: "removal",
  dbhGrowth: "removal",
  deadRate: "removal"
};
var AUDIT_REC_NON_BAND = ["fertMismatch", "transportDiff"];
function auditRecKeysFor(formKey) {
  return Object.keys(AUDIT_REC_FORM_KEY).filter((k) => AUDIT_REC_FORM_KEY[k] === formKey);
}

// src/metrics.ts
function extractMetrics(formKey, answers, areaHa) {
  const metrics = [];
  if (areaHa != null) {
    metrics.push({ metricKey: "area_ha", metricValue: areaHa, unit: "ha" });
  }
  if (formKey === "onfarm") {
    const coffeeTotal = CF_BANDS.reduce((sum, key) => sum + (num(answers[key]) ?? 0), 0);
    if (coffeeTotal > 0 && areaHa) {
      metrics.push({
        metricKey: "coffee_density_per_ha",
        metricValue: coffeeTotal / areaHa,
        unit: "g\u1ED1c/ha"
      });
    }
    const yieldTotal = num(answers["volumn"]);
    if (yieldTotal != null && areaHa) {
      metrics.push({
        metricKey: "yield_per_ha",
        metricValue: yieldTotal / areaHa,
        unit: "kg/ha"
      });
    }
    let inorganicTotal = 0;
    let hasInorganic = false;
    for (const prefix of repeatRows(answers, "fertilizer_time")) {
      const vol = num(answers[`${prefix}fertilizer_volumn`]);
      if (vol != null && answers[`${prefix}fertilizer_type`] !== "organic") {
        inorganicTotal += vol;
        hasInorganic = true;
      }
    }
    if (hasInorganic && areaHa) {
      metrics.push({
        metricKey: "fert_inorg_per_ha",
        metricValue: inorganicTotal / areaHa,
        unit: "kg/ha"
      });
    }
  }
  return metrics;
}

// src/index.ts
function runAudit(formKey, answers, config = DEFAULT_AUDIT_CONFIG, choices) {
  let findings = [];
  if (formKey === "onfarm") findings = checkOnFarmRules(answers, config, choices);
  else if (formKey === "removal") findings = checkRemovalRules(answers, config, choices);
  else return [];
  const disabled = config.disabledRules;
  if (disabled?.length) {
    const off = new Set(disabled);
    findings = findings.filter((f) => !off.has(f.code));
  }
  return applyRuleText(findings, config.ruleText);
}
export {
  AUDIT_REC,
  AUDIT_REC_FORM_KEY,
  AUDIT_REC_NON_BAND,
  AUDIT_RULE_BY_CODE,
  AUDIT_RULE_CATALOG,
  AUDIT_RULE_DEFAULT_TEXT,
  BAND_MAX_AGE,
  CF_BANDS,
  CONSTRAINT_CODE_PREFIX,
  DEFAULT_AUDIT_CONFIG,
  DEFAULT_CALL_RE,
  DEFAULT_SOIL_TYPE,
  DYNAMIC_LIST_PREFIX,
  DYNAMIC_MARK,
  ExprError,
  FORM_KEYS,
  FORM_LABELS,
  FORM_SUBJECT,
  FUEL_GROUPS,
  INTERCROP_SPECIES,
  KNOWN_DYNAMIC_LISTS,
  MASTER_DATA_KEYS,
  MAX_BOUNDARY_VERTICES,
  MAX_REPEAT_INSTANCES,
  MIDDLEMAN_TYPES,
  MIDDLEMAN_TYPE_LABELS,
  MIDDLEMEN_LIST,
  REQUIRED_CODE_PREFIX,
  RULE_TEXT_PLACEHOLDERS,
  SOIL_TYPES,
  SOIL_TYPE_INCLUDES,
  TREE_SPECS,
  YIELD_CURVE,
  applyRuleText,
  auditRecKeysFor,
  bool,
  boundaryAreaHa,
  boundaryProblem,
  boundaryVertices,
  buildKey,
  buildSurveyTree,
  catalogFor,
  checkCompleteness,
  checkConstraint,
  checkOnFarmRules,
  checkRemovalRules,
  choiceAllowed,
  countExistingRows,
  describeBoundary,
  dynamicListsOf,
  escapeRegExp,
  evalExpr,
  evalRelevant,
  expandRuleText,
  exprSyntaxError,
  expressionProblem,
  extractMetrics,
  formLabel,
  indexChoices,
  isCompletenessCode,
  isDynamicList,
  isFormKey,
  isLegacyConstraint,
  isMiddlemanType,
  isSoilType,
  legacyChoiceFilterVar,
  listOfQuestions,
  lookupChoice,
  masterDataAnswers,
  maxRowsOf,
  middlemanChoice,
  multi,
  num,
  parentPrefix,
  parseExpr,
  parseKey,
  polygonFromVertices,
  pruneIrrelevantAnswers,
  regionNames,
  repeatHasNamedRows,
  repeatRowLabel,
  repeatRows,
  requiredProgress,
  resolve,
  rowPrefix,
  runAudit,
  selectListOf,
  selfIntersects,
  splitLabelNumber,
  stripMasterDataKeys,
  surveyColumnLabel,
  surveyOutline,
  surveySectionId,
  usesNewEngine,
  withMasterData
};
